"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  Bell,
  Building2,
  CalendarDays,
  CheckCheck,
  ChevronRight,
  DollarSign,
  Download,
  Home,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  Pencil,
  Radio,
  RefreshCw,
  ShieldCheck,
  Target,
  Trophy,
  UserRound,
  Users,
  Wifi,
  WifiOff,
} from "lucide-react";
import { auth } from "./auth";
import { memberApi } from "./memberApi";
import { offlineStore } from "./offlineDb";
import { setContext, syncQueuedOperations } from "./api";
import { useInstallPrompt } from "./useInstallPrompt";
import { applyPlatformIdentity, platformIdentity } from "./platformIdentity";
import { matchLiveCopy, scoreboardLaunchUrl, scoreboardStatusLabel } from "./scoreboard";
import {
  applyScoresheetGameChange,
  createScoresheetState,
  hydrateScoresheet,
  isCricketScoresheetGame,
  matchScoresheetTotals,
  reviewScoresheet,
  scoresheetGameDisplayName,
  shouldApplyScoresheetRefresh,
} from "./scoresheet";

const routeFromLocation = () => globalThis.location?.pathname || "/";
const claimTokenFromLocation = () => {
  const match = routeFromLocation().match(/^\/claim\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};
const teamInviteTokenFromLocation = () => {
  const match = routeFromLocation().match(/^\/team-invite\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};
const teamJoinIdFromLocation = () => {
  const match = routeFromLocation().match(/^\/team-join\/([0-9a-f-]+)/i);
  return match ? match[1] : null;
};
const teamRegistrationIdFromLocation = () => {
  const match = routeFromLocation().match(/^\/register-team\/([0-9a-f-]+)/i);
  return match ? match[1] : null;
};
const tournamentIdFromLocation = () => {
  const match = routeFromLocation().match(/^\/tournaments\/([0-9a-f-]+)/i);
  return match ? match[1] : null;
};
const usPhoneDigits = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return (
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits
  ).slice(0, 10);
};
const usPhoneForApi = (value) => {
  const digits = usPhoneDigits(value);
  return digits.length === 10 ? `+1${digits}` : "";
};
const refreshApplication = async () => {
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    if (registration)
      await Promise.race([
        registration.update(),
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]);
  } catch {
    /* Reload still refreshes network-first app assets. */
  }
  globalThis.location?.reload();
};
const PullToRefresh = () => {
  const startY = useRef(null);
  const distanceRef = useRef(0);
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const threshold = 90;
  useEffect(() => {
    const start = (event) => {
      if (globalThis.scrollY > 0 || event.touches.length !== 1 || refreshing)
        return;
      startY.current = event.touches[0].clientY;
      distanceRef.current = 0;
    };
    const move = (event) => {
      if (startY.current == null || globalThis.scrollY > 0) return;
      const pull = Math.max(0, event.touches[0].clientY - startY.current);
      const next = Math.min(120, pull * 0.55);
      if (next > 4) event.preventDefault();
      distanceRef.current = next;
      setDistance(next);
    };
    const end = () => {
      startY.current = null;
      if (distanceRef.current >= threshold) {
        setRefreshing(true);
        setDistance(threshold);
        refreshApplication();
      } else {
        distanceRef.current = 0;
        setDistance(0);
      }
    };
    addEventListener("touchstart", start, { passive: true });
    addEventListener("touchmove", move, { passive: false });
    addEventListener("touchend", end, { passive: true });
    addEventListener("touchcancel", end, { passive: true });
    return () => {
      removeEventListener("touchstart", start);
      removeEventListener("touchmove", move);
      removeEventListener("touchend", end);
      removeEventListener("touchcancel", end);
    };
  }, [refreshing]);
  const ready = distance >= threshold;
  return (
    <div
      className={`pull-to-refresh ${distance > 0 || refreshing ? "visible" : ""} ${ready ? "ready" : ""} ${refreshing ? "refreshing" : ""}`}
      style={{ transform: `translate(-50%, ${distance - 54}px)` }}
      role="status"
      aria-live="polite"
    >
      <RefreshCw />
      <span>
        {refreshing
          ? "Refreshing…"
          : ready
            ? "Release to refresh"
            : "Pull to refresh"}
      </span>
    </div>
  );
};
const urlBase64ToUint8Array = (value) => {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const raw = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
};

const Brand = ({ compact = false }) => (
  <div className={compact ? "compact-brand" : "claim-brand"}>
    <span>◎</span>
    <b>OCHE</b>
    {!compact && <em>LEAGUE MANAGER</em>}
  </div>
);
const appVersion = __APP_VERSION__;
const scoresheetV2Enabled =
  String(
    import.meta.env.VITE_SCORESHEET_V2_ENABLED || "false",
  ).toLowerCase() === "true";
const scoreboardBaseUrl = (
  import.meta.env.VITE_SCOREBOARD_BASE_URL || "https://score.ocheleague.com"
).replace(/\/$/, "");
const LeagueIdentityHeader = ({
  organizationName,
  leagueName,
  online,
  offlineLabel = "Offline ready",
}) => {
  const primaryName = leagueName || organizationName || "Your League";
  return (
    <header className="league-app-header">
      <div className="league-identity">
        <strong>{primaryName}</strong>
        <small>
          <span aria-hidden="true">◎</span> Powered by{" "}
          {platformIdentity.productBrandName}
        </small>
      </div>
      <div className="connection-status">
        <div className={`connection-pill ${online ? "online" : "offline"}`}>
          {online ? <Wifi size={13} /> : <WifiOff size={13} />}
          {online ? "Synced" : offlineLabel}
        </div>
        <small>Version {appVersion}</small>
      </div>
    </header>
  );
};
const EmptyState = ({ icon, title, text }) => (
  <section className="empty-state">
    <div>{icon}</div>
    <h2>{title}</h2>
    <p>{text}</p>
  </section>
);
const NavButton = ({ active, prominent, onClick, icon, label }) => (
  <button
    className={`${active ? "active" : ""} ${prominent ? "prominent" : ""}`}
    onClick={onClick}
  >
    {icon}
    <span>{label}</span>
  </button>
);
const BrowserInstallRecommendation = ({ install }) => {
  const [dismissed, setDismissed] = useState(
    () =>
      sessionStorage.getItem("oche-install-recommendation-dismissed") ===
      "true",
  );
  if (install.installed || dismissed) return null;
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const dismiss = () => {
    sessionStorage.setItem("oche-install-recommendation-dismissed", "true");
    setDismissed(true);
  };
  return (
    <aside
      className="install-recommendation"
      aria-label="Install Oche League Manager"
    >
      <Download />
      <div>
        <b>Oche works best installed</b>
        <span>Faster match-night access, offline support, and app alerts.</span>
        {!install.available && (
          <small>
            {ios
              ? "Tap Share, then Add to Home Screen."
              : "Open your browser menu and choose Install app."}
          </small>
        )}
      </div>
      {install.available && (
        <button className="install-now" onClick={install.install}>
          Install
        </button>
      )}
      <button
        className="install-dismiss"
        aria-label="Dismiss installation recommendation"
        onClick={dismiss}
      >
        ×
      </button>
    </aside>
  );
};
const PerspectiveSwitch = ({ perspective, captainTeams, onChange }) => {
  const pending = captainTeams.reduce(
    (sum, team) => sum + (team.pendingRegistrationCount || 0),
    0,
  );
  return (
    <div
      className="perspective-switch"
      role="group"
      aria-label="Choose dashboard perspective"
    >
      <button
        className={perspective === "player" ? "active" : ""}
        onClick={() => onChange("player")}
      >
        <UserRound /> Player
      </button>
      <button
        className={perspective === "team" ? "active" : ""}
        onClick={() => onChange("team")}
      >
        <Users /> Team{pending > 0 && <span>{pending}</span>}
      </button>
    </div>
  );
};

function Welcome({ onContinue, install }) {
  const [location, setLocation] = useState("");
  const [discovery, setDiscovery] = useState(null);
  const [discoveryStatus, setDiscoveryStatus] = useState({
    loading: false,
    error: "",
  });

  const findLeagues = async (event) => {
    event.preventDefault();
    const searchLocation = location.trim();
    if (!searchLocation) {
      setDiscovery(null);
      setDiscoveryStatus({
        loading: false,
        error: "Enter a ZIP code or city to find nearby leagues.",
      });
      return;
    }

    setDiscovery(null);
    setDiscoveryStatus({ loading: true, error: "" });
    try {
      setDiscovery(await memberApi.discoverLeagues(searchLocation));
      setDiscoveryStatus({ loading: false, error: "" });
    } catch (error) {
      setDiscoveryStatus({
        loading: false,
        error: error.message || "We could not search for leagues right now.",
      });
    }
  };

  return (
    <main className="welcome-screen">
      <div className="welcome-glow" />
      <div className="brand-lockup">
        <span className="target-mark">◎</span>
        <b>OCHE</b>
        <span>LEAGUE</span>
      </div>
      <section className="welcome-copy">
        <span className="kicker">THE SHOW MUST GO ON</span>
        <h1>
          Your darts.
          <br />
          <em>Your night.</em>
        </h1>
        <p>
          Teams, stats, rosters, and match scoring—ready even when the pub Wi-Fi
          is not.
        </p>
      </section>
      <section
        className="league-discovery"
        aria-labelledby="find-leagues-heading"
      >
        <div className="league-discovery-heading">
          <MapPin aria-hidden="true" />
          <div>
            <span className="kicker">PLAY LOCAL</span>
            <h2 id="find-leagues-heading">Find leagues near you</h2>
          </div>
        </div>
        <form onSubmit={findLeagues}>
          <label htmlFor="league-location">ZIP code or city</label>
          <div className="league-discovery-search">
            <input
              id="league-location"
              autoComplete="postal-code"
              placeholder="Enter ZIP code"
              value={location}
              onChange={(event) => setLocation(event.target.value)}
            />
            <button type="submit" disabled={discoveryStatus.loading}>
              {discoveryStatus.loading ? "Searching…" : "Find leagues"}
            </button>
          </div>
        </form>
        {discoveryStatus.error && (
          <p className="league-discovery-error" role="alert">
            {discoveryStatus.error}
          </p>
        )}
        {discovery && (
          <div className="league-discovery-results" aria-live="polite">
            <p className="league-discovery-summary">
              {discovery.results.length
                ? `${discovery.results.length} ${discovery.results.length === 1 ? "league" : "leagues"} near ${discovery.resolvedLocation}`
                : `No published leagues found near ${discovery.resolvedLocation}. Try another ZIP code or check back soon.`}
            </p>
            {discovery.results.map((result) => (
              <article className="league-result" key={result.seasonId}>
                <div>
                  <b>{result.leagueName}</b>
                  <span>{result.organizationName}</span>
                </div>
                <strong>
                  {result.distanceMiles == null
                    ? result.state
                    : `${result.distanceMiles} mi`}
                </strong>
                <p>
                  {result.seasonName} {result.year} · {result.teamCount}{" "}
                  registered {result.teamCount === 1 ? "team" : "teams"}
                </p>
                <small>
                  <MapPin aria-hidden="true" />{" "}
                  {result.nearestPubName || `${result.city}, ${result.state}`}
                </small>
                {result.teamsAcceptingPlayers > 0 && (
                  <div className="league-recruiting-teams">
                    <em>
                      {result.teamsAcceptingPlayers}{" "}
                      {result.teamsAcceptingPlayers === 1
                        ? "team is"
                        : "teams are"}{" "}
                      recruiting
                    </em>
                    {(result.recruitingTeams || []).map((team) => (
                      <a
                        key={team.teamJoinPublicId}
                        href={`/team-join/${team.teamJoinPublicId}`}
                      >
                        <span>
                          <b>{team.teamName}</b>
                          <small>
                            {team.registrationStatus === "Registered"
                              ? "Registered team"
                              : "Building its roster"}
                            {team.homePubName ? ` · ${team.homePubName}` : ""}
                          </small>
                        </span>
                        <ChevronRight />
                      </a>
                    ))}
                  </div>
                )}
              </article>
            ))}
            <small className="league-discovery-attribution">
              {discovery.attribution}
            </small>
          </div>
        )}
      </section>
      <div className="welcome-actions">
        <button className="action-primary" onClick={onContinue}>
          Continue to {platformIdentity.productBrandName}{" "}
          <ChevronRight size={20} />
        </button>
        {!install.installed && (
          <button
            className="action-quiet"
            disabled={!install.available}
            onClick={install.install}
          >
            <Download size={17} />{" "}
            {install.available
              ? "Install on this phone"
              : "Install option appears in your browser menu"}
          </button>
        )}
      </div>
    </main>
  );
}

function SmsOptInDemo() {
  const [phone, setPhone] = useState("");
  const [consented, setConsented] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    applyPlatformIdentity();
    document.title = `SMS opt-in | ${platformIdentity.productBrandName}`;
  }, []);

  const submit = (event) => {
    event.preventDefault();
    setError("");
    if (!consented) {
      setError(
        "Select the optional SMS consent checkbox to demonstrate enrollment, or continue without SMS.",
      );
      return;
    }
    if (usPhoneDigits(phone).length !== 10) {
      setError("Enter a 10-digit U.S. mobile phone number.");
      return;
    }
    setResult("opted-in");
  };

  return (
    <main className="claim-screen sms-opt-in-screen">
      <Brand />
      <section className="claim-card sms-opt-in-card">
        <div className="claim-icon">
          <ShieldCheck />
        </div>
        <span className="kicker">PUBLIC CONSENT DEMONSTRATION</span>
        <h1>League text updates</h1>
        <p>
          This public page demonstrates the optional SMS consent flow used by
          players of {platformIdentity.productBrandName}, a product of{" "}
          {platformIdentity.legalBusinessName}. No login is required for
          compliance review.
        </p>

        {result ? (
          <div className="sms-opt-in-confirmation" role="status">
            <ShieldCheck />
            <div>
              <h2>
                {result === "opted-in"
                  ? "SMS preference confirmed"
                  : "SMS declined"}
              </h2>
              {result === "opted-in" ? (
                <>
                  <p>
                    <b>Confirmation message shown to the player:</b>
                  </p>
                  <blockquote>
                    {platformIdentity.productBrandName}: You are subscribed to
                    recurring league account and match updates. Message
                    frequency varies. Message and data rates may apply. Reply
                    HELP for help or STOP to cancel.
                  </blockquote>
                </>
              ) : (
                <p>
                  The player can continue using the service without receiving
                  SMS messages.
                </p>
              )}
              <button
                type="button"
                className="action-quiet"
                onClick={() => {
                  setResult("");
                  setConsented(false);
                  setPhone("");
                }}
              >
                Review the opt-in again
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <label>
              <span>
                Mobile phone number <small>U.S. numbers only</small>
              </span>
              <div className="us-phone-input">
                <span>+1</span>
                <input
                  aria-label="Mobile phone number"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength="10"
                  placeholder="8175551212"
                  value={usPhoneDigits(phone)}
                  onChange={(event) =>
                    setPhone(event.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                />
              </div>
            </label>
            <fieldset className="claim-preferences">
              <legend>Optional SMS consent</legend>
              <p>
                SMS is not required to register, play in a league, or use the
                service.
              </p>
              <label className="claim-consent">
                <input
                  type="checkbox"
                  checked={consented}
                  onChange={(event) => setConsented(event.target.checked)}
                />
                <span>
                  <b>
                    I agree to receive recurring SMS messages from{" "}
                    {platformIdentity.productBrandName}
                  </b>
                  <small>
                    Messages include player and team invitations, roster
                    changes, schedules, match reminders, fee notices, and
                    account notifications. Message frequency varies. Message and
                    data rates may apply. Reply HELP for help, email{" "}
                    {platformIdentity.productSupportEmail}, or reply STOP to
                    cancel. Consent is optional and is not a condition of
                    registration, purchase, or league participation.
                  </small>
                </span>
              </label>
              <p className="claim-legal">
                By opting in, you agree to the{" "}
                <a
                  href="https://api.ocheleague.com/legal/terms"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of Service
                </a>{" "}
                and acknowledge the{" "}
                <a
                  href="https://api.ocheleague.com/legal/privacy"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
                . SMS consent is not shared with third parties or affiliates for
                marketing or promotional purposes.
              </p>
            </fieldset>
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <button className="action-primary" type="submit">
              <span>Confirm SMS opt-in</span>
              <ChevronRight />
            </button>
            <button
              className="action-quiet sms-decline"
              type="button"
              onClick={() => {
                setError("");
                setResult("declined");
              }}
            >
              Continue without SMS
            </button>
          </form>
        )}

        <aside className="sms-review-details">
          <b>Messaging program details</b>
          <dl>
            <div>
              <dt>Brand</dt>
              <dd>{platformIdentity.productBrandName}</dd>
            </div>
            <div>
              <dt>Legal business</dt>
              <dd>{platformIdentity.legalBusinessName}</dd>
            </div>
            <div>
              <dt>Campaign use case</dt>
              <dd>Low Volume Mixed</dd>
            </div>
            <div>
              <dt>Support</dt>
              <dd>
                <a href={`mailto:${platformIdentity.productSupportEmail}`}>
                  {platformIdentity.productSupportEmail}
                </a>
              </dd>
            </div>
          </dl>
        </aside>
      </section>
    </main>
  );
}

function ClaimScreen({ token, account, onClaimed }) {
  const [preview, setPreview] = useState(null);
  const autoClaimStarted = useRef("");
  const [autoClaimAttempt, setAutoClaimAttempt] = useState(0);
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    emailOptIn: false,
    smsOptIn: false,
    preferredCommunicationChannel: "None",
  });
  const [status, setStatus] = useState({
    loading: true,
    saving: false,
    error: "",
  });
  const identityEmail =
    account?.username || account?.idTokenClaims?.email || "";

  useEffect(() => {
    if (!auth.configured) {
      const demo = {
        organizationName: "Oche League Demo",
        firstName: "Darrn",
        lastName: "Player",
        deliveryTarget: "demo@ocheleague.com",
        status: "Pending",
        expiresAt: new Date(Date.now() + 86400000).toISOString(),
      };
      setPreview(demo);
      setProfile({
        firstName: "Darrin",
        lastName: "Player",
        phone: "",
        emailOptIn: false,
        smsOptIn: false,
        preferredCommunicationChannel: "None",
      });
      setStatus({ loading: false, saving: false, error: "" });
      return;
    }
    memberApi
      .previewClaim(token)
      .then((value) => {
        setPreview(value);
        setProfile({
          firstName: value.firstName,
          lastName: value.lastName,
          phone: "",
          emailOptIn: false,
          smsOptIn: false,
          preferredCommunicationChannel: "None",
        });
        setStatus({ loading: false, saving: false, error: "" });
      })
      .catch((error) =>
        setStatus({ loading: false, saving: false, error: error.message }),
      );
  }, [token, account]);

  const expired =
    preview &&
    (preview.status !== "Pending" ||
      new Date(preview.expiresAt) <= new Date());
  const emailMismatch =
    preview?.deliveryChannel?.toLowerCase() === "email" &&
    preview.deliveryTarget &&
    identityEmail.trim().toLowerCase() !==
      preview.deliveryTarget.trim().toLowerCase();
  const autoClaimEligible =
    auth.configured &&
    preview &&
    account &&
    !expired &&
    !emailMismatch &&
    preview.deliveryChannel?.toLowerCase() === "email";

  useEffect(() => {
    if (!autoClaimEligible) return;
    const attemptKey = `${token}:${identityEmail.toLowerCase()}:${autoClaimAttempt}`;
    if (autoClaimStarted.current === attemptKey) return;
    autoClaimStarted.current = attemptKey;
    setStatus({ loading: false, saving: true, error: "" });
    memberApi
      .confirmClaim(token, {})
      .then(() => memberApi.account())
      .then(onClaimed)
      .catch((error) =>
        setStatus({ loading: false, saving: false, error: error.message }),
      );
  }, [
    account,
    autoClaimAttempt,
    autoClaimEligible,
    identityEmail,
    onClaimed,
    token,
  ]);

  if (status.loading)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>CHECKING INVITATION</b>
      </div>
    );
  if (!preview)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card error-card">
          <h1>Invitation unavailable</h1>
          <p>{status.error || "Ask your captain to send a new invitation."}</p>
          {account && (
            <div className="membership-actions">
              <button
                onClick={() => {
                  history.replaceState({}, "", "/");
                  location.reload();
                }}
              >
                <UserRound /> Player account
              </button>
              <button
                onClick={async () => {
                  await offlineStore.clearForSignOut();
                  await auth.signOut();
                }}
              >
                <LogOut /> Sign out
              </button>
            </div>
          )}
        </section>
      </main>
    );
  if (!account)
    return (
      <main className="claim-screen">
        <Brand />
        <section className={`claim-card${expired ? " error-card" : ""}`}>
          <div className="claim-icon">
            <Mail />
          </div>
          <span className="kicker">PLAYER INVITATION</span>
          <h1>Join {preview.organizationName}.</h1>
          <p>
            <b>{preview.firstName} {preview.lastName}</b>, create your free {" "}
            {platformIdentity.productBrandName} account to see your team,
            schedule, matches, and statistics.
          </p>
          {preview.deliveryTarget && (
            <p className="imported-name">
              Your invitation was sent to <b>{preview.deliveryTarget}</b>. We’ll
              send a one-time code to verify it.
            </p>
          )}
          {!expired && (
            <>
              <button
                className="action-primary"
                onClick={() => auth.signUp(`/claim/${token}`)}
              >
                <span>Create my Oche account</span>
                <ChevronRight size={19} />
              </button>
              <button
                className="action-quiet"
                onClick={() =>
                  auth.signIn(`/claim/${token}`, preview.deliveryTarget || "")
                }
              >
                <LogIn size={18} />
                I already have an Oche account
              </button>
            </>
          )}
          {expired && (
            <p className="form-error">
              This invitation has expired or was already used. Ask your captain
              to send a new invitation.
            </p>
          )}
        </section>
      </main>
    );
  if (emailMismatch)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card error-card">
          <div className="claim-icon">
            <UserRound />
          </div>
          <span className="kicker">WRONG SIGN-IN</span>
          <h1>Use the invited account</h1>
          <p>
            This invitation was sent to <b>{preview.deliveryTarget}</b>, but you
            are signed in as <b>{identityEmail}</b>.
          </p>
          <button
            className="action-primary"
            onClick={async () => {
              await offlineStore.clearForSignOut();
              await auth.signOut();
            }}
          >
            <span>Sign out and use {preview.deliveryTarget}</span>
            <LogOut />
          </button>
        </section>
      </main>
    );

  if (autoClaimEligible)
    return (
      <main className="claim-screen">
        <Brand />
        <section className={`claim-card${status.error ? " error-card" : ""}`}>
          <div className="claim-icon">
            {status.error ? <UserRound /> : <ShieldCheck />}
          </div>
          <span className="kicker">
            {status.error ? "CONNECTION INTERRUPTED" : "INVITATION VERIFIED"}
          </span>
          <h1>
            {status.error
              ? "We couldn’t finish connecting your account."
              : `Connecting you to ${preview.organizationName}…`}
          </h1>
          <p>
            {status.error
              ? status.error
              : "Your verified email matches the invitation. No additional confirmation is needed."}
          </p>
          {status.error && (
            <button
              className="action-primary"
              onClick={() => setAutoClaimAttempt((attempt) => attempt + 1)}
            >
              <span>Try again</span>
              <RefreshCw />
            </button>
          )}
        </section>
      </main>
    );

  return (
    <main className="claim-screen">
      <Brand />
      <section className="claim-card">
        <div className="claim-organization">
          <Building2 />
          <span>
            <small>INVITED TO</small>
            <b>{preview.organizationName}</b>
          </span>
        </div>
        <span className="kicker">CONFIRM YOUR PLAYER</span>
        <h1>Is this you?</h1>
        <p className="imported-name">
          Your roster currently shows{" "}
          <b>
            {preview.firstName} {preview.lastName}
          </b>
          . Correct it below if it was entered differently.
        </p>
        <div className="signed-in-identity">
          <UserRound />
          <span>
            <small>SIGNED IN AS</small>
            <b>{identityEmail}</b>
          </span>
          <button
            onClick={async () => {
              await offlineStore.clearForSignOut();
              await auth.signOut();
            }}
          >
            Not you?
          </button>
        </div>
        <label>
          <span>Email address</span>
          <input
            type="email"
            value={identityEmail}
            readOnly
            aria-readonly="true"
          />
        </label>
        <label>
          <span>First name</span>
          <input
            autoComplete="given-name"
            value={profile.firstName}
            onChange={(event) =>
              setProfile({ ...profile, firstName: event.target.value })
            }
          />
        </label>
        <label>
          <span>Last name</span>
          <input
            autoComplete="family-name"
            value={profile.lastName}
            onChange={(event) =>
              setProfile({ ...profile, lastName: event.target.value })
            }
          />
        </label>
        <label>
          <span>
            Mobile phone <small>optional</small>
          </span>
          <div className="us-phone-input">
            <span>+1</span>
            <input
              autoComplete="tel-national"
              inputMode="numeric"
              maxLength="10"
              placeholder="8175551212"
              value={usPhoneDigits(profile.phone)}
              onChange={(event) =>
                setProfile({
                  ...profile,
                  phone: event.target.value.replace(/\D/g, "").slice(0, 10),
                })
              }
            />
          </div>
        </label>
        <fieldset className="claim-preferences">
          <legend>Communication choices</legend>
          <p>
            Email is optional and is used when an important invitation cannot
            reach your in-app notification inbox.
          </p>
          <label className="claim-consent">
            <input
              type="checkbox"
              checked={profile.emailOptIn}
              onChange={(event) =>
                setProfile({
                  ...profile,
                  emailOptIn: event.target.checked,
                  smsOptIn: false,
                  preferredCommunicationChannel: event.target.checked
                    ? "Email"
                    : "None",
                })
              }
            />
            <span>
              <b>Email updates</b>
              <small>
                Receive league invitations, roster, schedule, fee, and
                match-related email.
              </small>
            </span>
          </label>
          <p className="claim-legal">
            After registration, open Updates to optionally turn on browser
            alerts. Review the{" "}
            <a
              href="https://api.ocheleague.com/legal/privacy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href="https://api.ocheleague.com/legal/terms"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Service
            </a>
            .
          </p>
        </fieldset>
        <div className="claim-proof">
          <ShieldCheck />
          <span>
            The invitation—not a name match—is the proof connecting this sign-in
            to the roster record.
          </span>
        </div>
        <button
          className="action-primary"
          disabled={
            expired ||
            !profile.firstName.trim() ||
            !profile.lastName.trim() ||
            status.saving
          }
          onClick={async () => {
            if (!auth.configured) {
              await offlineStore.setPreference(
                "organizationName",
                preview.organizationName,
              );
              onClaimed({ organizationName: preview.organizationName });
              return;
            }
            setStatus({ ...status, saving: true, error: "" });
            try {
              await memberApi.confirmClaim(token, {
                ...profile,
                phone: usPhoneForApi(profile.phone),
              });
              onClaimed(await memberApi.account());
            } catch (error) {
              setStatus({
                loading: false,
                saving: false,
                error: error.message,
              });
            }
          }}
        >
          <span>
            {status.saving
              ? "Connecting your player…"
              : "Claim my player record"}
          </span>
          <ChevronRight />
        </button>
        {expired && (
          <p className="form-error">
            This invitation has expired or was already used.
          </p>
        )}
        {status.error && <p className="form-error">{status.error}</p>}
      </section>
    </main>
  );
}

function TeamInvitationScreen({ token, account }) {
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState({
    loading: true,
    saving: false,
    error: "",
    complete: null,
  });
  const [claimVersion, setClaimVersion] = useState(0);

  useEffect(() => {
    setStatus((value) => ({ ...value, loading: true, error: "" }));
    if (!auth.configured) {
      setPreview({
        leagueName: "Oche League Demo",
        seasonName: "Demo Season",
        seasonYear: new Date().getFullYear(),
        teamName: "Demo Team",
        playerFirstName: "Demo",
        playerLastName: "Player",
        requiresPlayerClaim: false,
        status: "Pending",
        expiresAt: new Date(Date.now() + 86400000).toISOString(),
      });
      setStatus({ loading: false, saving: false, error: "", complete: null });
      return;
    }
    memberApi
      .previewTeamInvitation(token)
      .then((value) => {
        setPreview(value);
        setStatus({ loading: false, saving: false, error: "", complete: null });
      })
      .catch((error) =>
        setStatus({
          loading: false,
          saving: false,
          error: error.message,
          complete: null,
        }),
      );
  }, [token, account, claimVersion]);

  if (status.loading)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>CHECKING TEAM INVITATION</b>
      </div>
    );
  if (!preview)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card error-card">
          <h1>Invitation unavailable</h1>
          <p>{status.error || "Ask your captain to send a new invitation."}</p>
          {account && (
            <div className="membership-actions">
              <button
                onClick={() => {
                  history.replaceState({}, "", "/");
                  location.reload();
                }}
              >
                <UserRound /> Player account
              </button>
              <button
                onClick={async () => {
                  await offlineStore.clearForSignOut();
                  await auth.signOut();
                }}
              >
                <LogOut /> Sign out
              </button>
            </div>
          )}
        </section>
      </main>
    );
  const unavailable =
    preview.status !== "Pending" || new Date(preview.expiresAt) <= new Date();
  if (!account)
    return (
      <main className="claim-screen">
        <Brand />
        <section className={`claim-card${unavailable ? " error-card" : ""}`}>
          <div className="claim-icon">
            <Users />
          </div>
          <span className="kicker">TEAM INVITATION</span>
          <h1>Join {preview.teamName}.</h1>
          <p>
            <b>{preview.playerFirstName} {preview.playerLastName}</b>, your captain
            saved you a spot in {preview.leagueName}. Create your free account
            to connect your player record and respond.
          </p>
          {!unavailable && (
            <>
              <button
                className="action-primary"
                onClick={() => auth.signUp(`/team-invite/${token}`)}
              >
                <span>Create my Oche account</span>
                <ChevronRight size={19} />
              </button>
              <button
                className="action-quiet"
                onClick={() => auth.signIn(`/team-invite/${token}`)}
              >
                <LogIn size={18} />
                I already have an Oche account
              </button>
            </>
          )}
          {unavailable && (
            <p className="form-error">
              This invitation is no longer active. Ask your captain to send a
              new invitation.
            </p>
          )}
        </section>
      </main>
    );
  if (preview.requiresPlayerClaim)
    return (
      <ClaimScreen
        token={token}
        account={account}
        onClaimed={() => setClaimVersion((value) => value + 1)}
      />
    );
  if (status.complete)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card">
          <div className="claim-icon">
            <ShieldCheck />
          </div>
          <span className="kicker">{status.complete.status}</span>
          <h1>
            {status.complete.isRostered
              ? `Welcome to ${preview.teamName}.`
              : "Invitation declined."}
          </h1>
          <p>{status.complete.message}</p>
          <button
            className="action-primary"
            onClick={() => {
              history.replaceState({}, "", "/");
              location.reload();
            }}
          >
            <span>Open my dashboard</span>
            <ChevronRight />
          </button>
        </section>
      </main>
    );
  const respond = async (accept) => {
    setStatus((value) => ({ ...value, saving: true, error: "" }));
    try {
      const result = accept
        ? await memberApi.acceptTeamInvitation(token)
        : await memberApi.rejectTeamInvitation(token);
      setStatus({ loading: false, saving: false, error: "", complete: result });
    } catch (error) {
      setStatus((value) => ({ ...value, saving: false, error: error.message }));
    }
  };
  return (
    <main className="claim-screen">
      <Brand />
      <section className="claim-card">
        <div className="claim-organization">
          <Users />
          <span>
            <small>TEAM INVITATION</small>
            <b>{preview.leagueName}</b>
          </span>
        </div>
        <span className="kicker">
          {preview.seasonName} {preview.seasonYear}
        </span>
        <h1>Join {preview.teamName}?</h1>
        <p>
          <b>
            {preview.playerFirstName} {preview.playerLastName}
          </b>
          , accept to take this roster spot. A player may accept only one team
          in the same league season.
        </p>
        <div className="signed-in-identity">
          <UserRound />
          <span>
            <small>SIGNED IN AS</small>
            <b>{account?.username || account?.idTokenClaims?.email}</b>
          </span>
          <button
            onClick={async () => {
              await offlineStore.clearForSignOut();
              await auth.signOut();
            }}
          >
            Not you?
          </button>
        </div>
        {!unavailable && (
          <>
            <button
              className="action-primary"
              disabled={status.saving}
              onClick={() => respond(true)}
            >
              <span>
                {status.saving ? "Saving…" : "Accept team invitation"}
              </span>
              <ChevronRight />
            </button>
            <button
              className="action-quiet"
              disabled={status.saving}
              onClick={() => respond(false)}
            >
              Decline — remain available for another team
            </button>
          </>
        )}
        {unavailable && (
          <div className="invitation-unavailable">
            <b>
              {preview.status === "Accepted"
                ? "This roster spot was already accepted."
                : preview.status === "Rejected"
                  ? "This invitation was declined."
                  : preview.status === "Expired"
                    ? "This invitation has expired."
                    : "This invitation is no longer active."}
            </b>
            <span>
              You are not trapped here. Open your player account or sign out to
              use another account.
            </span>
            <div className="membership-actions">
              <button
                onClick={() => {
                  history.replaceState({}, "", "/");
                  location.reload();
                }}
              >
                <UserRound /> Player account
              </button>
              <button
                onClick={async () => {
                  await offlineStore.clearForSignOut();
                  await auth.signOut();
                }}
              >
                <LogOut /> Sign out
              </button>
            </div>
          </div>
        )}
        {status.error && <p className="form-error">{status.error}</p>}
      </section>
    </main>
  );
}

function TeamJoinScreen({ publicId, account }) {
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState({
    loading: true,
    saving: false,
    error: "",
    complete: null,
  });

  useEffect(() => {
    memberApi
      .previewTeamJoin(publicId)
      .then((value) => {
        setPreview(value);
        setStatus({ loading: false, saving: false, error: "", complete: null });
      })
      .catch((error) =>
        setStatus({
          loading: false,
          saving: false,
          error: error.message,
          complete: null,
        }),
      );
  }, [publicId]);

  if (status.loading)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>CHECKING TEAM</b>
      </div>
    );
  if (!preview)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card error-card">
          <h1>Team unavailable</h1>
          <p>
            {status.error || "This recruiting link is no longer available."}
          </p>
          <button
            className="action-primary"
            onClick={() => {
              history.replaceState({}, "", "/");
              location.reload();
            }}
          >
            <span>Find another league</span>
            <ChevronRight />
          </button>
        </section>
      </main>
    );
  if (status.complete)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card">
          <div className="claim-icon">
            <CheckCheck />
          </div>
          <span className="kicker">REQUEST SENT</span>
          <h1>{preview.teamName} received your request.</h1>
          <p>{status.complete.message}</p>
          <button
            className="action-primary"
            onClick={() => {
              history.replaceState({}, "", "/");
              location.reload();
            }}
          >
            <span>Open my dashboard</span>
            <ChevronRight />
          </button>
        </section>
      </main>
    );
  const unavailable = !preview.isSeasonActive || !preview.isAcceptingPlayers;
  const requestToJoin = async () => {
    setStatus((value) => ({ ...value, saving: true, error: "" }));
    try {
      await setContext({
        organizationId: preview.organizationId,
        organizationName: preview.organizationName,
      });
      const result = await memberApi.requestTeamJoin(publicId);
      setStatus({ loading: false, saving: false, error: "", complete: result });
    } catch (error) {
      setStatus((value) => ({ ...value, saving: false, error: error.message }));
    }
  };
  return (
    <main className="claim-screen">
      <Brand />
      <section className="claim-card">
        <div className="claim-organization">
          <Users />
          <span>
            <small>{preview.leagueName}</small>
            <b>{preview.organizationName}</b>
          </span>
        </div>
        <span className="kicker">
          {preview.seasonName} {preview.seasonYear}
        </span>
        <h1>Request to join {preview.teamName}</h1>
        <p>
          This team is building its roster for {preview.flightName}. The captain
          will review your request before you are added.
        </p>
        {!account && !unavailable && (
          <button
            className="action-primary"
            onClick={() => auth.signIn(`/team-join/${publicId}`)}
          >
            <span>Sign in to request a spot</span>
            <LogIn />
          </button>
        )}
        {account && !unavailable && (
          <button
            className="action-primary"
            disabled={status.saving}
            onClick={requestToJoin}
          >
            <span>
              {status.saving ? "Sending request…" : "Ask to join this team"}
            </span>
            <ChevronRight />
          </button>
        )}
        {unavailable && (
          <div className="invitation-unavailable">
            <b>This team is not currently accepting player requests.</b>
            <span>
              Return to league discovery to find another recruiting team.
            </span>
          </div>
        )}
        {status.error && <p className="form-error">{status.error}</p>}
      </section>
    </main>
  );
}

function OrganizationPicker({ organizations, onSelect }) {
  return (
    <main className="organization-screen">
      <Brand />
      <section>
        <span className="kicker">YOUR DARTS COMMUNITY</span>
        <h1>Choose an organization</h1>
        <p>
          Your player identity travels with you. Each organization keeps its own
          teams, fees, and membership.
        </p>
        <div className="organization-list">
          {organizations.map((organization) => (
            <button
              key={organization.organizationId}
              onClick={() => onSelect(organization)}
            >
              <Building2 />
              <span>
                <b>{organization.organizationName}</b>
                <small>{organization.role}</small>
              </span>
              <ChevronRight />
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}

function TeamRegistrationScreen({ publicId, account }) {
  const newTeamId = useRef(crypto.randomUUID());
  const [season, setSeason] = useState(null);
  const [teams, setTeams] = useState([]);
  const [choice, setChoice] = useState("new");
  const [form, setForm] = useState({
    teamName: "",
    flightId: "",
    homePubId: "",
    captainFirstName: "",
    captainLastName: "",
    captainPhone: "",
  });
  const [captainProfile, setCaptainProfile] = useState(null);
  const [status, setStatus] = useState({
    loading: true,
    saving: false,
    error: "",
    complete: null,
  });

  useEffect(() => {
    (async () => {
      try {
        const details = await memberApi.teamRegistration(publicId);
        setSeason(details);
        if (details.flights.length === 1)
          setForm((value) => ({ ...value, flightId: details.flights[0].id }));
        if (account) {
          const profile =
            await memberApi.captainProfileForRegistration(publicId);
          setCaptainProfile(profile);
          setForm((value) => ({
            ...value,
            captainFirstName: profile.firstName,
            captainLastName: profile.lastName,
            captainPhone: usPhoneDigits(profile.phoneNumber),
          }));
          setTeams(
            (await memberApi.captainTeamsForRegistration(publicId)).filter(
              (team) => !team.alreadyRegistered,
            ),
          );
        }
        setStatus((value) => ({ ...value, loading: false }));
      } catch (error) {
        setStatus({
          loading: false,
          saving: false,
          error: error.message,
          complete: null,
        });
      }
    })();
  }, [publicId, account]);

  if (status.loading)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>OPENING REGISTRATION</b>
      </div>
    );
  if (!season)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card error-card">
          <h1>Registration link unavailable</h1>
          <p>
            {status.error || "Ask the league organizer for a current link."}
          </p>
        </section>
      </main>
    );
  if (status.complete)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card registration-complete">
          <div className="claim-icon">
            <ShieldCheck />
          </div>
          <span className="kicker">TEAM CREATED</span>
          <h1>{status.complete.teamName} is ready.</h1>
          <p>
            You are the team captain. Build your roster, invite players, and
            submit the team when at least six players are ready.
          </p>
          <button
            className="action-primary"
            onClick={() => {
              history.replaceState({}, "", "/");
              location.reload();
            }}
          >
            Manage my team <ChevronRight />
          </button>
        </section>
      </main>
    );

  return (
    <main className="claim-screen team-registration-screen">
      <Brand />
      <section className="claim-card">
        <div className="claim-organization">
          <Trophy />
          <span>
            <small>REGISTER FOR</small>
            <b>{season.organizationName}</b>
          </span>
        </div>
        <span className="kicker">
          {season.leagueName} · {season.seasonName} {season.year}
        </span>
        <h1>Bring your team.</h1>
        <p>
          {season.registrationAvailable
            ? "Sign in, choose a playing flight, and become the captain of your team."
            : "Team registration is not currently open for this season."}
        </p>
        {!account ? (
          <button
            className="action-primary"
            disabled={!season.registrationAvailable}
            onClick={() => auth.signIn(`/register-team/${publicId}`)}
          >
            <span>Sign up or log in</span>
            <LogIn />
          </button>
        ) : (
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              const submitted = new FormData(event.currentTarget);
              const captainFirstName = String(
                submitted.get("captainFirstName") || form.captainFirstName,
              ).trim();
              const captainLastName = String(
                submitted.get("captainLastName") || form.captainLastName,
              ).trim();
              const captainPhone = usPhoneForApi(
                submitted.get("captainPhone") || form.captainPhone,
              );
              setForm((value) => ({
                ...value,
                captainFirstName,
                captainLastName,
                captainPhone: usPhoneDigits(captainPhone),
              }));
              setStatus((value) => ({ ...value, saving: true, error: "" }));
              try {
                const result = await memberApi.registerCaptainTeam(publicId, {
                  teamId: choice === "new" ? null : choice,
                  newTeamId: choice === "new" ? newTeamId.current : null,
                  teamName: choice === "new" ? form.teamName : "",
                  flightId: form.flightId,
                  homePubId: form.homePubId || null,
                  captainFirstName,
                  captainLastName,
                  captainPhone,
                });
                await setContext({
                  organizationId: season.organizationId,
                  organizationName: season.organizationName,
                });
                setStatus({
                  loading: false,
                  saving: false,
                  error: "",
                  complete: result,
                });
              } catch (error) {
                setStatus((value) => ({
                  ...value,
                  saving: false,
                  error: error.message,
                }));
              }
            }}
          >
            {captainProfile && !captainProfile.isComplete && (
              <fieldset className="captain-profile-fields">
                <legend>Your captain profile</legend>
                <p>
                  Entra only supplied your sign-in email. Tell Oche who will own
                  this team.
                </p>
                <label>
                  <span>Email address</span>
                  <input type="email" value={captainProfile.email} readOnly />
                </label>
                <label>
                  <span>First name</span>
                  <input
                    name="captainFirstName"
                    required
                    maxLength="100"
                    autoComplete="given-name"
                    value={form.captainFirstName}
                    onChange={(event) =>
                      setForm({ ...form, captainFirstName: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>Last name</span>
                  <input
                    name="captainLastName"
                    required
                    maxLength="100"
                    autoComplete="family-name"
                    value={form.captainLastName}
                    onChange={(event) =>
                      setForm({ ...form, captainLastName: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>
                    Phone <small>optional</small>
                  </span>
                  <div className="us-phone-input">
                    <span>+1</span>
                    <input
                      name="captainPhone"
                      maxLength="10"
                      autoComplete="tel-national"
                      inputMode="numeric"
                      placeholder="8175551212"
                      value={usPhoneDigits(form.captainPhone)}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          captainPhone: event.target.value
                            .replace(/\D/g, "")
                            .slice(0, 10),
                        })
                      }
                    />
                  </div>
                </label>
              </fieldset>
            )}
            {teams.length > 0 && (
              <label>
                <span>Team</span>
                <select
                  value={choice}
                  onChange={(event) => setChoice(event.target.value)}
                >
                  <option value="new">Create a new team</option>
                  {teams.map((team) => (
                    <option value={team.id} key={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {choice === "new" && (
              <label>
                <span>Team name</span>
                <input
                  required
                  maxLength="200"
                  value={form.teamName}
                  onChange={(event) =>
                    setForm({ ...form, teamName: event.target.value })
                  }
                  placeholder="The Board Members"
                />
              </label>
            )}
            <label>
              <span>Division / flight</span>
              <select
                required
                value={form.flightId}
                onChange={(event) =>
                  setForm({ ...form, flightId: event.target.value })
                }
              >
                <option value="">Choose a playing night</option>
                {season.flights.map((flight) => (
                  <option key={flight.id} value={flight.id}>
                    {flight.division} · {flight.flight} · {flight.night}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>
                Home pub <small>optional</small>
              </span>
              <select
                value={form.homePubId}
                onChange={(event) =>
                  setForm({ ...form, homePubId: event.target.value })
                }
              >
                <option value="">Choose later</option>
                {season.pubs.map((pub) => (
                  <option key={pub.id} value={pub.id}>
                    {pub.name}
                    {pub.city ? ` · ${pub.city}` : ""}
                  </option>
                ))}
              </select>
            </label>
            {status.error && <p className="form-error">{status.error}</p>}
            <button
              className="action-primary"
              disabled={
                !season.registrationAvailable ||
                !form.flightId ||
                (captainProfile &&
                  !captainProfile.isComplete &&
                  (!form.captainFirstName.trim() ||
                    !form.captainLastName.trim())) ||
                status.saving
              }
            >
              <span>
                {status.saving
                  ? "Creating your team…"
                  : choice === "new"
                    ? "Create my team"
                    : "Register selected team"}
              </span>
              <ChevronRight />
            </button>
          </form>
        )}
      </section>
    </main>
  );
}

const tournamentDateLabel = (value) =>
  new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));

const tournamentStatusCopy = (registration) =>
  registration?.status === "Waitlisted"
    ? `Waitlist position ${registration.waitlistPosition}`
    : registration?.status;

function TournamentRegistrationScreen({ publicId, account }) {
  const [tournament, setTournament] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [status, setStatus] = useState({
    loading: true,
    saving: false,
    error: "",
  });

  const load = async () => {
    setStatus((value) => ({ ...value, loading: true, error: "" }));
    try {
      const details = await memberApi.tournament(publicId);
      setTournament(details);
      if (account) {
        const registrations = await memberApi.tournamentRegistrations();
        setRegistration(
          registrations.find(
            (value) => value.publicId.toLowerCase() === publicId.toLowerCase(),
          ) || null,
        );
      }
      setStatus({ loading: false, saving: false, error: "" });
    } catch (error) {
      setStatus({ loading: false, saving: false, error: error.message });
    }
  };

  useEffect(() => {
    load();
  }, [publicId, account]);
  if (status.loading)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>OPENING TOURNAMENT</b>
      </div>
    );
  if (!tournament)
    return (
      <main className="claim-screen">
        <Brand />
        <section className="claim-card error-card">
          <span className="kicker">TOURNAMENT</span>
          <h1>This event is not available.</h1>
          <p>
            {account
              ? status.error ||
                "The link may have expired or the event may be private."
              : "This may be a private organization tournament. Sign in to check your eligibility."}
          </p>
          {!account && (
            <button
              className="action-primary"
              onClick={() => auth.signIn(`/tournaments/${publicId}`)}
            >
              <span>Sign in to check</span>
              <LogIn />
            </button>
          )}
        </section>
      </main>
    );

  const activeRegistration =
    registration && !["Withdrawn", "Rejected"].includes(registration.status);
  const spotsRemaining = Math.max(
    0,
    tournament.capacity - tournament.acceptedCount,
  );
  const save = async (action) => {
    setStatus((value) => ({ ...value, saving: true, error: "" }));
    try {
      const result =
        action === "register"
          ? await memberApi.registerForTournament(publicId)
          : await memberApi.withdrawFromTournament(publicId);
      setRegistration(result);
      setStatus({ loading: false, saving: false, error: "" });
      const details = await memberApi.tournament(publicId);
      setTournament(details);
    } catch (error) {
      setStatus({ loading: false, saving: false, error: error.message });
    }
  };

  return (
    <main className="claim-screen tournament-registration-screen">
      <Brand />
      <section className="claim-card tournament-registration-card">
        <div className="claim-organization">
          <Trophy />
          <span>
            <small>HOSTED BY</small>
            <b>{tournament.organizationName}</b>
          </span>
        </div>
        <span className="kicker">
          {tournament.visibility === "OrganizationOnly"
            ? "PRIVATE ORGANIZATION EVENT"
            : "PUBLIC TOURNAMENT"}
        </span>
        <h1>{tournament.name}</h1>
        {tournament.description && <p>{tournament.description}</p>}
        <div className="tournament-facts">
          <div>
            <CalendarDays />
            <span>
              <small>STARTS</small>
              <b>{tournamentDateLabel(tournament.startsAt)}</b>
            </span>
          </div>
          <div>
            <MapPin />
            <span>
              <small>PLAYING AT</small>
              <b>{tournament.hostPubName || "Venue to be announced"}</b>
              {(tournament.city || tournament.state) && (
                <em>
                  {[tournament.city, tournament.state]
                    .filter(Boolean)
                    .join(", ")}
                </em>
              )}
            </span>
          </div>
          <div>
            <Users />
            <span>
              <small>FIELD</small>
              <b>
                {spotsRemaining > 0
                  ? `${spotsRemaining} of ${tournament.capacity} spots remain`
                  : `${tournament.capacity}-player field is full`}
              </b>
            </span>
          </div>
          <div>
            <DollarSign />
            <span>
              <small>ENTRY</small>
              <b>
                {Number(tournament.entryFee) > 0
                  ? new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: tournament.currency || "USD",
                    }).format(tournament.entryFee)
                  : "Free entry"}
              </b>
            </span>
          </div>
        </div>
        {activeRegistration ? (
          <div
            className={`tournament-registration-status ${registration.status.toLowerCase()}`}
          >
            <ShieldCheck />
            <span>
              <small>YOUR REGISTRATION</small>
              <b>{tournamentStatusCopy(registration)}</b>
              <em>
                {registration.status === "Pending"
                  ? "The organizer will review your entry."
                  : registration.status === "Accepted"
                    ? "You're in. See you at the oche."
                    : "We'll move you in automatically when a spot opens."}
              </em>
            </span>
          </div>
        ) : !account ? (
          <button
            className="action-primary"
            disabled={!tournament.registrationOpen}
            onClick={() => auth.signIn(`/tournaments/${publicId}`)}
          >
            <span>
              {tournament.registrationOpen
                ? "Sign in to register"
                : "Registration is closed"}
            </span>
            <LogIn />
          </button>
        ) : (
          <button
            className="action-primary"
            disabled={!tournament.registrationOpen || status.saving}
            onClick={() => save("register")}
          >
            <span>
              {status.saving
                ? "Saving your spot…"
                : tournament.requiresOrganizerApproval
                  ? "Request my spot"
                  : "Register for tournament"}
            </span>
            <ChevronRight />
          </button>
        )}
        {activeRegistration && (
          <button
            className="action-quiet tournament-withdraw"
            disabled={status.saving}
            onClick={() => save("withdraw")}
          >
            {status.saving ? "Updating…" : "Withdraw my registration"}
          </button>
        )}
        {!tournament.registrationOpen && !activeRegistration && (
          <p className="tournament-window">
            Registration is not currently open.
          </p>
        )}
        {status.error && (
          <p className="form-error" role="alert">
            {status.error}
          </p>
        )}
      </section>
    </main>
  );
}

function TournamentRegistrationsView({ registrations, loading, onOpen }) {
  return (
    <section className="section-stack">
      <div className="section-heading">
        <span className="kicker">YOUR EVENTS</span>
        <h1>My tournaments</h1>
        <p>Registration status and tournament-day details in one place.</p>
      </div>
      {loading ? (
        <div className="launch-inline">Loading tournaments…</div>
      ) : registrations.length === 0 ? (
        <EmptyState
          icon={<Trophy />}
          title="No tournaments yet"
          text="When you register through an organizer's tournament link, it will appear here."
        />
      ) : (
        registrations.map((item) => (
          <button
            className="tournament-list-card"
            key={item.id}
            onClick={() => onOpen(item.publicId)}
          >
            <div className="date-tile">
              <b>{new Date(item.startsAt).getDate()}</b>
              <span>
                {new Date(item.startsAt).toLocaleString("en-US", {
                  month: "short",
                })}
              </span>
            </div>
            <span>
              <small>{item.organizationName}</small>
              <b>{item.tournamentName}</b>
              <em>
                <MapPin size={13} />{" "}
                {item.hostPubName || "Venue to be announced"}
              </em>
            </span>
            <strong className={item.status.toLowerCase()}>
              {tournamentStatusCopy(item)}
            </strong>
            <ChevronRight />
          </button>
        ))
      )}
    </section>
  );
}

function InviteNeeded({ account, online, install }) {
  const [section, setSection] = useState("player");
  const email =
    account?.username || account?.idTokenClaims?.email || "Signed-in player";
  return (
    <>
      <BrowserInstallRecommendation install={install} />
      <div className="mobile-shell unconnected-account">
        <LeagueIdentityHeader
          organizationName="Oche Player Account"
          online={online}
        />
        <main className="app-content">
          {section === "player" && (
            <section className="section-stack">
              <div className="greeting">
                <span>PLAYER DASHBOARD</span>
                <h1>Welcome to Oche</h1>
                <p>{email}</p>
              </div>
              <article className="detail-card">
                <header>
                  <UserRound />
                  <h2>Connect your player record</h2>
                </header>
                <p className="muted-copy">
                  Open the invitation from your captain to connect the exact
                  player record and continue joining the team. If you already
                  opened it, return to the original email and tap the invitation
                  link again.
                </p>
              </article>
              <div className="claim-proof">
                <ShieldCheck />
                <span>
                  We use the private invitation instead of matching by name,
                  preventing another player’s history from being connected to
                  your account.
                </span>
              </div>
            </section>
          )}
          {section === "notifications" && (
            <section className="section-stack notification-center">
              <div className="section-heading">
                <span className="kicker">YOUR UPDATES</span>
                <h1>Notifications</h1>
                <p>
                  Your league and team updates will remain available here after
                  your player record is connected.
                </p>
              </div>
              <EmptyState
                icon={<Bell />}
                title="No updates yet"
                text="Finish the invitation to connect your league notifications to this account."
              />
            </section>
          )}
          {section === "account" && (
            <section className="section-stack">
              <div className="profile-hero">
                <div className="avatar">{email[0]?.toUpperCase()}</div>
                <span className="kicker">SIGNED IN</span>
                <h1>Player account</h1>
                <p>{email}</p>
              </div>
              <div className="account-actions single-action">
                <button
                  onClick={async () => {
                    await offlineStore.clearForSignOut();
                    await auth.signOut();
                  }}
                >
                  <LogOut /> Sign out
                </button>
              </div>
            </section>
          )}
        </main>
        <nav
          className="bottom-nav account-bottom-nav"
          aria-label="Account navigation"
        >
          <NavButton
            active={section === "player"}
            onClick={() => setSection("player")}
            icon={<UserRound />}
            label="Player"
          />
          <NavButton
            active={section === "notifications"}
            onClick={() => setSection("notifications")}
            icon={<Bell />}
            label="Updates"
          />
          <NavButton
            active={section === "account"}
            onClick={() => setSection("account")}
            icon={<LogOut />}
            label="Account"
          />
        </nav>
      </div>
    </>
  );
}

function MembershipRequired({ onSwitch }) {
  return (
    <main className="organization-screen">
      <Brand />
      <section>
        <span className="kicker">ONE MORE STEP</span>
        <h1>Connect your player record</h1>
        <p>
          Your sign-in can access this organization, but it is not connected to
          a registered player yet.
        </p>
        <div className="membership-steps">
          <b>If you are also a player</b>
          <span>
            In the Control Panel, open Players, select your player record, and
            create an invitation. Open that invitation link on this phone to
            connect the exact record.
          </span>
        </div>
        <div className="claim-proof">
          <ShieldCheck />
          <span>
            We do not link by name alone because spelling and duplicate names
            can connect the wrong statistics.
          </span>
        </div>
        <div className="membership-actions">
          <button onClick={onSwitch}>
            <Building2 /> Choose another organization
          </button>
          <button
            onClick={async () => {
              await offlineStore.clearForSignOut();
              await auth.signOut();
            }}
          >
            <LogOut /> Sign out
          </button>
        </div>
      </section>
    </main>
  );
}

function CaptainWorkspace({
  teams,
  organizationName,
  online,
  onPlayerView,
  onSwitch,
}) {
  const [teamId, setTeamId] = useState(teams[0]?.id || "");
  const [detail, setDetail] = useState(null);
  const [registrationId, setRegistrationId] = useState("");
  const [section, setSection] = useState("home");
  const [roster, setRoster] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [available, setAvailable] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [newPlayer, setNewPlayer] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [emailLookup, setEmailLookup] = useState(null);
  const [forceAcceptPlayer, setForceAcceptPlayer] = useState(false);
  const [rosterEmailEditor, setRosterEmailEditor] = useState(null);
  const [savingRosterEmail, setSavingRosterEmail] = useState(false);
  const [delegateScope, setDelegateScope] = useState("Team");
  const [delegateMatchId, setDelegateMatchId] = useState("");
  const [notice, setNotice] = useState("");
  const [invitation, setInvitation] = useState(null);
  const refresh = async (
    selectedTeam = teamId,
    selectedRegistration = registrationId,
  ) => {
    const nextDetail = await memberApi.captainTeam(selectedTeam);
    setDetail(nextDetail);
    const visibleRegistrations = nextDetail.registrations.filter(
      (value) => value.isSeasonActive,
    );
    const preferred =
      visibleRegistrations.find((value) => value.status !== "Registered") ||
      visibleRegistrations[0] ||
      nextDetail.registrations[0];
    const nextRegistration =
      selectedRegistration &&
      nextDetail.registrations.some(
        (value) => value.id === selectedRegistration,
      )
        ? selectedRegistration
        : preferred?.id || "";
    setRegistrationId(nextRegistration);
    setDelegations(await memberApi.delegations(selectedTeam));
    if (nextRegistration) {
      const [nextRoster, nextAvailable, nextJoinRequests] = await Promise.all([
        memberApi.captainRoster(nextRegistration),
        memberApi.availableMembers(nextRegistration),
        memberApi.captainJoinRequests(nextRegistration),
      ]);
      setRoster(nextRoster);
      setAvailable(nextAvailable);
      setJoinRequests(nextJoinRequests);
    } else {
      setRoster([]);
      setAvailable([]);
      setJoinRequests([]);
    }
  };
  useEffect(() => {
    if (teamId) refresh(teamId, "").catch((error) => setNotice(error.message));
  }, [teamId]);
  if (!detail)
    return (
      <div className="launch-screen">
        <b>OPENING CAPTAIN TOOLS</b>
      </div>
    );
  const registration = detail.registrations.find(
    (value) => value.id === registrationId,
  );
  const activeRegistrations = detail.registrations.filter(
    (value) => value.isSeasonActive,
  );
  const registrationOptions = activeRegistrations.length
    ? activeRegistrations
    : detail.registrations;
  const registrationMatches = detail.matches.filter(
    (match) => match.teamRegistrationId === registrationId,
  );
  const nextMatch = registrationMatches[0];
  const acceptedRoster = roster.filter(
    (player) => player.rosterStatus === "Accepted",
  );
  const missingPlayers = Math.max(0, 6 - acceptedRoster.length);
  const registrationPending =
    registration && registration.status !== "Registered";
  const matchForView = nextMatch
    ? { ...nextMatch, matchId: nextMatch.id }
    : null;
  const captainMemberTeams = registration
    ? [{ registrationId: registration.id, isCaptain: true }]
    : [];
  const submitRegistration = async () => {
    try {
      await memberApi.submitTeamRegistration(registration.id);
      setNotice("Team registration submitted to the league.");
      await refresh();
    } catch (error) {
      setNotice(error.message);
    }
  };
  const setAcceptingPlayers = async (isAcceptingPlayers) => {
    try {
      await memberApi.setCaptainTeamAcceptingPlayers(
        registration.id,
        isAcceptingPlayers,
      );
      setNotice(
        isAcceptingPlayers
          ? "Your team is now visible to players looking for a team."
          : "Player requests are closed for this team.",
      );
      await refresh();
    } catch (error) {
      setNotice(error.message);
    }
  };
  const respondToJoinRequest = async (requestId, approve) => {
    try {
      const result = approve
        ? await memberApi.approveCaptainJoinRequest(registration.id, requestId)
        : await memberApi.rejectCaptainJoinRequest(registration.id, requestId);
      setNotice(result.message);
      await refresh();
    } catch (error) {
      setNotice(error.message);
    }
  };
  return (
    <main className="captain-workspace">
      <LeagueIdentityHeader
        organizationName={organizationName}
        leagueName={registration?.leagueName}
        online={online}
        offlineLabel="Offline"
      />
      <PerspectiveSwitch
        perspective="team"
        captainTeams={teams}
        onChange={(value) => value === "player" && onPlayerView()}
      />
      <section className="captain-hero">
        <span className="kicker">TEAM DASHBOARD</span>
        {teams.length > 1 && (
          <select
            aria-label="Choose team"
            value={teamId}
            onChange={(event) => {
              setDetail(null);
              setTeamId(event.target.value);
              setSection("home");
            }}
          >
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        )}
        <h1>{detail.name}</h1>
        <p>
          Manage registration, season roster, and match night from one place.
        </p>
      </section>
      {registrationOptions.length > 1 && (
        <label className="captain-select">
          <span>Active season</span>
          <select
            value={registrationId}
            onChange={(event) =>
              refresh(teamId, event.target.value).catch((error) =>
                setNotice(error.message),
              )
            }
          >
            {registrationOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.seasonName} {item.year} · {item.flightName}
              </option>
            ))}
          </select>
        </label>
      )}
      {registration ? (
        <>
          {section === "home" && (
            <section className="captain-overview">
              <article
                className={`captain-task-card ${registrationPending ? "needs-action" : "complete"}`}
              >
                <div>
                  <span className="kicker">
                    {registrationPending
                      ? "REGISTRATION IN PROGRESS"
                      : "SEASON READY"}
                  </span>
                  <h2>
                    {registration.seasonName} {registration.year}
                  </h2>
                  <p>
                    {registration.divisionName} · {registration.flightName}
                    {registration.playingNight
                      ? ` · ${registration.playingNight}`
                      : ""}
                  </p>
                </div>
                <strong>{registration.status}</strong>
                {registrationPending && missingPlayers > 0 ? (
                  <button
                    className="action-primary"
                    onClick={() => setSection("roster")}
                  >
                    <span>
                      Add {missingPlayers} more player
                      {missingPlayers === 1 ? "" : "s"}
                    </span>
                    <ChevronRight />
                  </button>
                ) : registrationPending ? (
                  <button
                    className="action-primary"
                    onClick={submitRegistration}
                  >
                    <span>Submit team registration</span>
                    <ChevronRight />
                  </button>
                ) : (
                  <div className="registration-ready">
                    <ShieldCheck />
                    <span>
                      Registration submitted. You can still add or drop players
                      during the active season.
                    </span>
                  </div>
                )}
              </article>
              <article className="captain-card captain-recruitment-card">
                <header>
                  <div>
                    <span className="kicker">PLAYER RECRUITMENT</span>
                    <h2>
                      {registration.isAcceptingPlayers
                        ? "Accepting requests"
                        : "Requests closed"}
                    </h2>
                  </div>
                  <button
                    className={`recruitment-toggle ${registration.isAcceptingPlayers ? "active" : ""}`}
                    onClick={() =>
                      setAcceptingPlayers(!registration.isAcceptingPlayers)
                    }
                  >
                    {registration.isAcceptingPlayers ? "Close" : "Open"}
                  </button>
                </header>
                <p>
                  {registration.isAcceptingPlayers
                    ? "Players can find this team in public league discovery and ask to join."
                    : "This team is hidden from recruiting results. You can reopen requests at any time."}
                </p>
                {joinRequests.length > 0 ? (
                  <div className="captain-join-requests">
                    <b>
                      {joinRequests.length} pending{" "}
                      {joinRequests.length === 1 ? "request" : "requests"}
                    </b>
                    {joinRequests.map((request) => (
                      <div className="captain-player" key={request.id}>
                        <span>
                          <b>
                            {request.firstName} {request.lastName}
                          </b>
                          <small>Member #{request.membershipIdNumber}</small>
                        </span>
                        <div>
                          <button
                            onClick={() =>
                              respondToJoinRequest(request.id, false)
                            }
                          >
                            Decline
                          </button>
                          <button
                            className="approve"
                            onClick={() =>
                              respondToJoinRequest(request.id, true)
                            }
                          >
                            Approve
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <small className="no-join-requests">
                    No pending player requests.
                  </small>
                )}
              </article>
              <button
                className="captain-match-card"
                onClick={() => setSection("match")}
              >
                <span className="kicker">NEXT MATCH</span>
                <h2>
                  {nextMatch
                    ? `${nextMatch.isHome ? "vs" : "at"} ${nextMatch.opponentName}`
                    : "Schedule coming soon"}
                </h2>
                <p>
                  {nextMatch
                    ? `${dateLabel(nextMatch.matchDate)} · ${nextMatch.pubName || "Venue to be announced"}`
                    : "Your next match will appear when the schedule is published."}
                </p>
                <span>
                  Open match night <ChevronRight />
                </span>
              </button>
              <div className="captain-summary-grid">
                <button onClick={() => setSection("roster")}>
                  <b>{acceptedRoster.length}</b>
                  <span>Accepted players</span>
                </button>
                <button onClick={() => setSection("tools")}>
                  <b>{delegations.length}</b>
                  <span>Captain helpers</span>
                </button>
              </div>
              <div className="account-actions">
                <button onClick={onSwitch}>
                  <Building2 /> Switch organization
                </button>
              </div>
            </section>
          )}
          {section === "roster" && (
            <section className="captain-card captain-roster-card">
              <span className="kicker">
                {registration.seasonName} {registration.year}
              </span>
              <h2>Season roster</h2>
              <p>
                {registration.status === "Registered"
                  ? "Add or drop players as the active season changes."
                  : `${acceptedRoster.length} of 6 required players accepted. Pending players can accept themselves, or you can accept for them on match night.`}
              </p>
              {roster.map((player) => (
                <div className="captain-player-contact" key={player.rosterMemberId}>
                  <div className="captain-player">
                    <span>
                      <b>
                        {player.firstName} {player.lastName}
                      </b>
                      <small>
                        {player.rosterStatus === "Accepted"
                          ? "Accepted roster player"
                          : `${player.rosterStatus} invitation${player.invitationExpiresAt ? ` · expires ${dateLabel(player.invitationExpiresAt.slice(0, 10))}` : ""}`}
                      </small>
                      <small className="roster-contact-email">
                        {player.email || "No email on file"}
                      </small>
                    </span>
                    <div>
                      {player.claimStatus !== "Claimed" && (
                        <button
                          onClick={() =>
                            setRosterEmailEditor({
                              playerMembershipId: player.playerMembershipId,
                              email: player.email || "",
                            })
                          }
                        >
                          {player.email ? "Email / invite" : "Add email & invite"}
                        </button>
                      )}
                      {player.rosterStatus === "Pending" && (
                        <button
                          className="approve"
                          onClick={async () => {
                            await memberApi.forceAcceptRosterMember(
                              registration.id,
                              player.playerMembershipId,
                            );
                            setNotice(
                              `${player.firstName} ${player.lastName} is accepted and can play tonight.`,
                            );
                            await refresh();
                          }}
                        >
                          Accept now
                        </button>
                      )}
                      <button
                        title="Remove player"
                        onClick={async () => {
                          await memberApi.removeRosterMember(
                            registration.id,
                            player.playerMembershipId,
                          );
                          await refresh();
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  {rosterEmailEditor?.playerMembershipId === player.playerMembershipId && (
                    <form
                      className="captain-roster-email-form"
                      onSubmit={async (event) => {
                        event.preventDefault();
                        const email = rosterEmailEditor.email.trim().toLowerCase();
                        setSavingRosterEmail(true);
                        try {
                          await memberApi.updateRosterMemberEmail(
                            registration.id,
                            player.playerMembershipId,
                            email,
                          );
                          const claim = await memberApi.inviteRosterPlayer(
                            player.playerMembershipId,
                            "Email",
                            email,
                          );
                          await memberApi.sendRosterPlayerInvitation(
                            player.playerMembershipId,
                            claim,
                          );
                          setInvitation({
                            invitationUrl: claim.claimUrl,
                            deliveryChannel: "Email",
                            deliveryTarget: email,
                            requiresChannelChoice: false,
                          });
                          setRosterEmailEditor(null);
                          setNotice(
                            `${player.firstName} ${player.lastName}'s email was saved and their account invitation was sent.`,
                          );
                          await refresh();
                        } catch (error) {
                          setNotice(error.message);
                        } finally {
                          setSavingRosterEmail(false);
                        }
                      }}
                    >
                      <label>
                        <span>Email address</span>
                        <input
                          required
                          type="email"
                          autoComplete="email"
                          value={rosterEmailEditor.email}
                          onChange={(event) =>
                            setRosterEmailEditor({
                              ...rosterEmailEditor,
                              email: event.target.value,
                            })
                          }
                          placeholder="player@example.com"
                        />
                      </label>
                      <button type="button" onClick={() => setRosterEmailEditor(null)}>
                        Cancel
                      </button>
                      <button className="approve" disabled={savingRosterEmail}>
                        {savingRosterEmail ? "Sending…" : "Save & send invite"}
                      </button>
                    </form>
                  )}
                </div>
              ))}
              {invitation && (
                <div className="captain-notice">
                  <b>
                    {invitation.deliveryChannel === "Email"
                      ? `Invitation email queued for ${invitation.deliveryTarget}.`
                      : invitation.requiresChannelChoice
                        ? "Choose how to send this invitation."
                        : "Invitation link created."}
                  </b>
                  <button
                    onClick={async () => {
                      await navigator.clipboard.writeText(
                        invitation.invitationUrl,
                      );
                      setNotice("Invitation link copied.");
                    }}
                  >
                    Copy invitation link
                  </button>
                  {invitation.requiresChannelChoice && (
                    <small>
                      SMS may be chosen only because this player already
                      consented. Copy the link now, or add the player again and
                      choose their preferred method.
                    </small>
                  )}
                </div>
              )}
              {available.length > 0 && (
                <div className="add-existing">
                  <select defaultValue="">
                    <option value="" disabled>
                      Invite an organization member
                    </option>
                    {available.map((player) => (
                      <option
                        key={player.playerMembershipId}
                        value={player.playerMembershipId}
                      >
                        {player.firstName} {player.lastName}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={async (event) => {
                      const select = event.currentTarget.previousElementSibling;
                      if (!select.value) return;
                      let result = await memberApi.addRosterMember(
                        registration.id,
                        select.value,
                      );
                      if (result.requiresChannelChoice) {
                        const channel = confirm(
                          "This player consented to SMS but has not selected a preference. Press OK to send SMS, or Cancel to send email.",
                        )
                          ? "Sms"
                          : "Email";
                        result = await memberApi.addRosterMember(
                          registration.id,
                          select.value,
                          channel,
                        );
                      }
                      setInvitation(result);
                      setNotice(
                        "Player invited. Their roster place remains pending until accepted.",
                      );
                      await refresh();
                    }}
                  >
                    Invite
                  </button>
                </div>
              )}
              <details>
                <summary>Add a new player for tonight</summary>
                <form
                  className="captain-new-player"
                  autoComplete="on"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    const form = event.currentTarget;
                    const submitted = new FormData(form);
                    const player = {
                      firstName: String(
                        submitted.get("firstName") || "",
                      ).trim(),
                      lastName: String(submitted.get("lastName") || "").trim(),
                      email: String(submitted.get("email") || "").trim(),
                      phone: usPhoneForApi(submitted.get("phone")),
                      forceAccept: submitted.get("forceAccept") === "on",
                    };
                    setNewPlayer({
                      firstName: player.firstName,
                      lastName: player.lastName,
                      email: player.email,
                      phone: usPhoneDigits(player.phone),
                    });
                    try {
                      const result = emailLookup?.found
                        ? await memberApi.addRosterMember(
                            registration.id,
                            emailLookup.playerMembershipId,
                            null,
                            player.forceAccept,
                          )
                        : await memberApi.addNewRosterPlayer(
                            registration.id,
                            player,
                          );
                      setInvitation(result.invitationUrl ? result : null);
                      setNewPlayer({
                        firstName: "",
                        lastName: "",
                        email: "",
                        phone: "",
                      });
                      setEmailLookup(null);
                      setForceAcceptPlayer(false);
                      form.reset();
                      await refresh();
                      setNotice(
                        `${player.firstName} ${player.lastName} was added${player.forceAccept || (!player.email && !player.phone) ? " and accepted for tonight" : ". Their acceptance is pending"}.`,
                      );
                    } catch (error) {
                      setNotice(error.message);
                    }
                  }}
                >
                  <label>
                    <span>First name</span>
                    <input
                      name="firstName"
                      required
                      autoComplete="given-name"
                      placeholder="First name"
                      value={newPlayer.firstName}
                      onChange={(event) =>
                        setNewPlayer({
                          ...newPlayer,
                          firstName: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    <span>Last name</span>
                    <input
                      name="lastName"
                      required
                      autoComplete="family-name"
                      placeholder="Last name"
                      value={newPlayer.lastName}
                      onChange={(event) =>
                        setNewPlayer({
                          ...newPlayer,
                          lastName: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    <span>
                      Email{" "}
                      <small>required only when the name already exists</small>
                    </span>
                    <input
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="Email (optional)"
                      value={newPlayer.email}
                      onChange={(event) => {
                        setNewPlayer({
                          ...newPlayer,
                          email: event.target.value,
                        });
                        setEmailLookup(null);
                      }}
                      onBlur={async (event) => {
                        const email = event.target.value.trim();
                        if (!email) return;
                        try {
                          setEmailLookup(
                            await memberApi.memberByEmail(
                              registration.id,
                              email,
                            ),
                          );
                        } catch (error) {
                          setNotice(error.message);
                        }
                      }}
                    />
                  </label>
                  {emailLookup?.found && (
                    <div
                      className={`captain-notice ${emailLookup.isAvailable ? "" : "error"}`}
                    >
                      <b>
                        {emailLookup.firstName} {emailLookup.lastName} ·{" "}
                        {emailLookup.membershipDisplayId}
                      </b>
                      <small>
                        {emailLookup.isAvailable
                          ? "Existing organization player found. Submitting will use this record."
                          : "This player is already assigned to a team in this season."}
                      </small>
                    </div>
                  )}
                  <label>
                    <span>
                      Phone <small>optional</small>
                    </span>
                    <div className="us-phone-input">
                      <span>+1</span>
                      <input
                        name="phone"
                        autoComplete="tel-national"
                        inputMode="numeric"
                        maxLength="10"
                        placeholder="Phone (optional)"
                        value={usPhoneDigits(newPlayer.phone)}
                        onChange={(event) =>
                          setNewPlayer({
                            ...newPlayer,
                            phone: event.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10),
                          })
                        }
                      />
                    </div>
                  </label>
                  <label className="claim-consent">
                    <input
                      name="forceAccept"
                      type="checkbox"
                      checked={forceAcceptPlayer}
                      onChange={(event) => setForceAcceptPlayer(event.target.checked)}
                    />
                    <span>
                      <b>Accept for this player now</b>
                      <small>
                        Use when the player cannot accept on a device. No
                        contact details automatically uses this match-night
                        exception.
                      </small>
                    </span>
                  </label>
                  <button
                    className="action-primary"
                    disabled={emailLookup?.found && !emailLookup.isAvailable}
                  >
                    {emailLookup?.found
                      ? "Use this player record"
                      : "Add player"}
                  </button>
                </form>
              </details>
              {registrationPending && acceptedRoster.length >= 6 && (
                <button
                  className="action-primary roster-submit"
                  onClick={submitRegistration}
                >
                  <span>Submit completed registration</span>
                  <ChevronRight />
                </button>
              )}
            </section>
          )}
          {section === "match" && (
            <MatchPrepView
              match={matchForView}
              teams={captainMemberTeams}
              online={online}
              showCurrentMatchStatus
            />
          )}
          {section === "tools" && (
            <section className="captain-card">
              <span className="kicker">DELEGATION</span>
              <h2>Captain helpers</h2>
              <p>
                Delegates can create lineups and approve completed matches. They
                cannot change the roster, invite players, submit registration,
                or appoint another delegate.
              </p>
              {delegations.map((item) => (
                <div className="captain-player" key={item.id}>
                  <span>
                    <b>{item.playerName}</b>
                    <small>
                      {item.scope === "Team"
                        ? "Permanent until revoked"
                        : "This match only"}
                    </small>
                  </span>
                  <button
                    onClick={async () => {
                      await memberApi.revokeDelegation(teamId, item.id);
                      await refresh();
                    }}
                  >
                    Revoke
                  </button>
                </div>
              ))}
              <div className="delegate-options">
                <select
                  value={delegateScope}
                  onChange={(event) => setDelegateScope(event.target.value)}
                >
                  <option value="Team">Permanent delegation</option>
                  <option value="Match">One match only</option>
                </select>
                {delegateScope === "Match" && (
                  <select
                    required
                    value={delegateMatchId}
                    onChange={(event) => setDelegateMatchId(event.target.value)}
                  >
                    <option value="">Choose a match</option>
                    {registrationMatches.map((match) => (
                      <option key={match.id} value={match.id}>
                        {match.matchDate} · Round {match.roundNumber} vs{" "}
                        {match.opponentName}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="add-existing">
                <select defaultValue="">
                  <option value="" disabled>
                    Choose an accepted roster player
                  </option>
                  {acceptedRoster.map((player) => (
                    <option
                      key={player.playerMembershipId}
                      value={player.playerMembershipId}
                    >
                      {player.firstName} {player.lastName}
                    </option>
                  ))}
                </select>
                <button
                  onClick={async (event) => {
                    const select = event.currentTarget.previousElementSibling;
                    if (
                      !select.value ||
                      (delegateScope === "Match" && !delegateMatchId)
                    ) {
                      setNotice("Choose both a player and match.");
                      return;
                    }
                    await memberApi.grantDelegation(teamId, {
                      playerMembershipId: select.value,
                      scope: delegateScope,
                      scheduledMatchId:
                        delegateScope === "Match" ? delegateMatchId : null,
                      expiresAt: null,
                    });
                    await refresh();
                  }}
                >
                  Delegate
                </button>
              </div>
            </section>
          )}
        </>
      ) : (
        <EmptyState
          icon={<CalendarDays />}
          title="No season registration"
          text="Use an open season registration link to enter this team."
        />
      )}
      {notice && <p className="captain-notice">{notice}</p>}
      <nav
        className="bottom-nav captain-dashboard-nav"
        aria-label="Team dashboard navigation"
      >
        <NavButton
          active={section === "home"}
          onClick={() => setSection("home")}
          icon={<Home />}
          label="Team"
        />
        <NavButton
          active={section === "roster"}
          onClick={() => setSection("roster")}
          icon={<Users />}
          label="Roster"
        />
        <NavButton
          prominent
          active={section === "match"}
          onClick={() => setSection("match")}
          icon={<Radio />}
          label="Match"
        />
        <NavButton
          active={section === "tools"}
          onClick={() => setSection("tools")}
          icon={<ShieldCheck />}
          label="Helpers"
        />
      </nav>
    </main>
  );
}

const demoDashboard = {
  organizationName: "Metro Darts Association",
  leagueName: "Metro Dart League",
  profile: {
    firstName: "Demo",
    lastName: "Player",
    email: "demo@ocheleague.com",
    phone: "",
    membershipDisplayId: "OCHE-1042",
    status: "Active",
  },
  teams: [
    {
      registrationId: "demo-team",
      teamName: "The Board Members",
      leagueName: "Metro Dart League",
      seasonName: "Fall 2026",
      flightName: "Monday A Flight",
      playingNight: "Monday",
      homePub: "The Brass Tap",
      isCaptain: true,
      captainName: "Demo Player",
    },
  ],
  matches: [
    {
      matchId: "demo-match",
      matchDate: new Date(Date.now() + 86400000 * 3).toISOString().slice(0, 10),
      roundNumber: 4,
      isHome: true,
      opponentName: "Triple Trouble",
      pubName: "The Brass Tap",
      isBye: false,
    },
  ],
  statistics: {
    gamesPlayed: 28,
    wins: 18,
    losses: 10,
    qualityPoints: 12,
    bestOut: 96,
    winPercentage: 64.3,
  },
  fees: [
    {
      id: "demo-fee",
      description: "Annual membership",
      coverageLabel: "2026",
      amount: 30,
      paid: 30,
      balance: 0,
      status: "Paid",
    },
  ],
  recentResults: [
    {
      id: "demo-result",
      status: "Final",
      recordedAt: new Date().toISOString(),
      summary: "7–4",
    },
  ],
};

const money = (value) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    value || 0,
  );
const dateLabel = (value) =>
  new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(new Date(`${value}T12:00:00`));

function ScheduleView({ matches }) {
  if (!matches.length)
    return (
      <EmptyState
        icon={<CalendarDays />}
        title="Your schedule"
        text="No upcoming matches are scheduled for your active teams."
      />
    );
  return (
    <section className="section-stack">
      <div className="section-heading">
        <span className="kicker">UPCOMING</span>
        <h1>Your schedule</h1>
      </div>
      {matches.map((match) => (
        <article className="list-card" key={match.matchId}>
          <div className="date-tile">
            <b>{new Date(`${match.matchDate}T12:00:00`).getDate()}</b>
            <span>
              {new Date(`${match.matchDate}T12:00:00`).toLocaleString("en-US", {
                month: "short",
              })}
            </span>
          </div>
          <div>
            <small>
              ROUND {match.roundNumber} · {match.isHome ? "HOME" : "AWAY"}
            </small>
            <h2>{match.isBye ? "Bye week" : match.opponentName}</h2>
            <p>
              <MapPin size={13} /> {match.pubName || "Venue to be announced"}
            </p>
          </div>
        </article>
      ))}
    </section>
  );
}

function NotificationsView({ onUnreadChanged }) {
  const [page, setPage] = useState({ unreadCount: 0, items: [] });
  const [preferences, setPreferences] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [notice, setNotice] = useState("Loading your updates…");
  const load = async () => {
    try {
      const [messages, savedPreferences, available] = await Promise.all([
        memberApi.notifications(),
        memberApi.notificationPreferences(),
        memberApi.notificationCapabilities(),
      ]);
      setPage(messages);
      setPreferences(savedPreferences);
      setCapabilities(available);
      setNotice("");
      onUnreadChanged(messages.unreadCount);
    } catch (error) {
      setNotice(error.message);
    }
  };
  useEffect(() => {
    load();
  }, []);
  const open = async (notification) => {
    if (!notification.readAt) {
      await memberApi.markNotificationRead(notification.id);
      setPage((value) => ({
        unreadCount: Math.max(0, value.unreadCount - 1),
        items: value.items.map((item) =>
          item.id === notification.id
            ? { ...item, readAt: new Date().toISOString() }
            : item,
        ),
      }));
      onUnreadChanged(Math.max(0, page.unreadCount - 1));
    }
    if (notification.actionUrl) location.href = notification.actionUrl;
  };
  const markAll = async () => {
    await memberApi.markAllNotificationsRead();
    setPage((value) => ({
      unreadCount: 0,
      items: value.items.map((item) => ({
        ...item,
        readAt: item.readAt || new Date().toISOString(),
      })),
    }));
    onUnreadChanged(0);
  };
  const enablePush = async () => {
    try {
      if (!capabilities?.pushAvailable)
        throw new Error(
          "Browser alerts are not enabled on this environment yet.",
        );
      if (!("serviceWorker" in navigator) || !("PushManager" in window))
        throw new Error(
          "This browser does not support app alerts. On iPhone, install this site to the Home Screen first.",
        );
      const registration = await navigator.serviceWorker.ready;
      const permission = await Notification.requestPermission();
      if (permission !== "granted")
        throw new Error(
          "Notification permission was not granted. You can still see every update in this inbox.",
        );
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          capabilities.vapidPublicKey,
        ),
      });
      const json = subscription.toJSON();
      await memberApi.savePushSubscription({
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
        deviceName: navigator.userAgent.includes("iPhone") ? "iPhone" : "Phone",
      });
      const saved = await memberApi.saveNotificationPreferences({
        ...preferences,
        pushEnabled: true,
      });
      setPreferences(saved);
      setNotice("App alerts are on for this device.");
    } catch (error) {
      setNotice(error.message);
    }
  };
  const disablePush = async () => {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await memberApi.removePushSubscription(subscription.endpoint);
      await subscription.unsubscribe();
    }
    const saved = await memberApi.saveNotificationPreferences({
      ...preferences,
      pushEnabled: false,
    });
    setPreferences(saved);
    setNotice("App alerts are off. Updates will remain in this inbox.");
  };
  return (
    <section className="section-stack notification-center">
      <div className="section-heading">
        <span className="kicker">LEAGUE UPDATES</span>
        <h1>Notifications</h1>
        <p>
          Invitations, roster changes, schedules, and match activity stay here
          even if your phone is offline.
        </p>
      </div>
      <article className="notification-settings">
        <div>
          <b>App alerts</b>
          <small>
            {preferences?.pushEnabled
              ? "On for this device"
              : "Optional — your inbox always works"}
          </small>
        </div>
        <button
          className="small-button"
          onClick={preferences?.pushEnabled ? disablePush : enablePush}
        >
          {preferences?.pushEnabled ? "Turn off" : "Turn on"}
        </button>
      </article>
      {notice && <p className="notification-notice">{notice}</p>}
      {page.unreadCount > 0 && (
        <button className="mark-all-button" onClick={markAll}>
          <CheckCheck /> Mark all read
        </button>
      )}
      <div className="notification-list">
        {page.items.map((item) => (
          <button
            key={item.id}
            className={`notification-card ${item.readAt ? "read" : "unread"}`}
            onClick={() => open(item)}
          >
            <span className="notification-dot" />
            <span>
              <small>
                {item.category} ·{" "}
                {new Date(item.createdAt).toLocaleDateString()}
              </small>
              <b>{item.title}</b>
              <p>{item.body}</p>
            </span>
            {item.actionUrl && <ChevronRight />}
          </button>
        ))}
      </div>
      {!notice && page.items.length === 0 && (
        <EmptyState
          icon={<Bell />}
          title="You're all caught up"
          text="New league and team updates will appear here."
        />
      )}
    </section>
  );
}

function ProfileView({ dashboard, onSaved, onSwitch }) {
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({
    emailOptIn: false,
    smsOptIn: false,
    preferredCommunicationChannel: "None",
    ...dashboard.profile,
    phone: usPhoneDigits(dashboard.profile.phone),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const stats = dashboard.statistics;
  const due = dashboard.fees.reduce((sum, fee) => sum + fee.balance, 0);
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const saved = await onSaved({
        ...profile,
        phone: usPhoneForApi(profile.phone),
        smsOptIn: false,
        preferredCommunicationChannel: profile.emailOptIn ? "Email" : "None",
      });
      setProfile((value) => ({
        ...value,
        ...saved,
        phone: usPhoneDigits(saved?.phone ?? value.phone),
      }));
      setEditing(false);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="section-stack">
      <div className="profile-hero">
        <div className="avatar">
          {profile.firstName?.[0]}
          {profile.lastName?.[0]}
        </div>
        <span className="kicker">{profile.membershipDisplayId}</span>
        <h1>
          {profile.firstName} {profile.lastName}
        </h1>
        <p>{profile.status} member</p>
        <button className="small-button" onClick={() => setEditing(!editing)}>
          <Pencil size={14} /> Edit profile
        </button>
      </div>
      {editing && (
        <form className="profile-form" onSubmit={save}>
          <label>
            First name
            <input
              required
              value={profile.firstName}
              onChange={(event) =>
                setProfile({ ...profile, firstName: event.target.value })
              }
            />
          </label>
          <label>
            Last name
            <input
              required
              value={profile.lastName}
              onChange={(event) =>
                setProfile({ ...profile, lastName: event.target.value })
              }
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={profile.email || ""}
              onChange={(event) =>
                setProfile({ ...profile, email: event.target.value })
              }
            />
          </label>
          <label>
            Phone
            <div className="us-phone-input">
              <span>+1</span>
              <input
                inputMode="numeric"
                autoComplete="tel-national"
                maxLength="10"
                placeholder="8175551212"
                value={usPhoneDigits(profile.phone)}
                onChange={(event) =>
                  setProfile({
                    ...profile,
                    phone: event.target.value.replace(/\D/g, "").slice(0, 10),
                  })
                }
              />
            </div>
          </label>
          <fieldset className="claim-preferences">
            <legend>Communication choices</legend>
            <label className="claim-consent">
              <input
                type="checkbox"
                checked={profile.emailOptIn}
                onChange={(event) =>
                  setProfile({
                    ...profile,
                    emailOptIn: event.target.checked,
                    smsOptIn: false,
                    preferredCommunicationChannel: event.target.checked
                      ? "Email"
                      : "None",
                  })
                }
              />
              <span>
                <b>Email updates</b>
                <small>
                  Receive important league, roster, schedule, fee, and
                  match-related email when an app alert is unavailable.
                </small>
              </span>
            </label>
            <p className="claim-legal">
              Browser alerts are managed from Updates. Your in-app inbox remains
              available without opting in.
            </p>
          </fieldset>
          {error && <p className="form-error">{error}</p>}
          <button className="action-primary" disabled={saving}>
            {saving ? "Saving…" : "Save profile & preferences"}
          </button>
        </form>
      )}
      <div className="stat-grid">
        <div>
          <b>{stats.gamesPlayed}</b>
          <span>Games</span>
        </div>
        <div>
          <b>{stats.wins}</b>
          <span>Wins</span>
        </div>
        <div>
          <b>{stats.winPercentage}%</b>
          <span>Win rate</span>
        </div>
        <div>
          <b>{stats.bestOut || "—"}</b>
          <span>Best out</span>
        </div>
      </div>
      {!!stats.recognizedStats?.length && (
        <article className="detail-card recognized-stats">
          <header>
            <Trophy />
            <h2>Season achievements</h2>
          </header>
          {stats.recognizedStats.map((stat) => (
            <div
              className="detail-row"
              key={`${stat.seasonId}-${stat.gameStatPolicyId}`}
            >
              <span>
                <b>{stat.name}</b>
                <small>Best qualifying turn: {stat.bestValue}</small>
              </span>
              <strong>
                {stat.total} {stat.shortLabel}
              </strong>
            </div>
          ))}
        </article>
      )}
      <article className="detail-card">
        <header>
          <DollarSign />
          <h2>Fees</h2>
          <strong className={due > 0 ? "due" : "paid"}>
            {due > 0 ? `${money(due)} due` : "Paid up"}
          </strong>
        </header>
        {dashboard.fees.length ? (
          dashboard.fees.map((fee) => (
            <div className="detail-row" key={fee.id}>
              <span>
                <b>{fee.description}</b>
                <small>{fee.coverageLabel}</small>
              </span>
              <strong>{fee.balance ? money(fee.balance) : "Paid"}</strong>
            </div>
          ))
        ) : (
          <p className="muted-copy">No fees have been assessed.</p>
        )}
      </article>
      <article className="detail-card">
        <header>
          <Users />
          <h2>Your teams</h2>
        </header>
        {dashboard.teams.map((team) => (
          <div className="detail-row" key={team.registrationId}>
            <span>
              <b>{team.teamName}</b>
              <small>
                {team.seasonName} · {team.flightName}
              </small>
            </span>
            {team.isCaptain && <strong>Captain</strong>}
          </div>
        ))}
      </article>
      <div className="account-actions profile-account-actions">
        <button onClick={refreshApplication}>
          <RefreshCw /> Refresh app
        </button>
        <button onClick={onSwitch}>
          <Building2 /> Switch organization
        </button>
        <button
          onClick={async () => {
            await offlineStore.clearForSignOut();
            await auth.signOut();
          }}
        >
          <LogOut /> Sign out
        </button>
      </div>
    </section>
  );
}

function MatchPrepView({ match, teams, online, showCurrentMatchStatus = false }) {
  const [matchPackage, setMatchPackage] = useState(null);
  const [status, setStatus] = useState("");
  const [entryMode, setEntryMode] = useState(null);
  const [evidenceNotice, setEvidenceNotice] = useState("");
  const [editingLineupId, setEditingLineupId] = useState(null);
  const [scoreboardGames, setScoreboardGames] = useState(null);
  const [liveMatch, setLiveMatch] = useState(null);
  const [liveMatchUnavailable, setLiveMatchUnavailable] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  useEffect(() => {
    offlineStore.getPreference("organizationId").then(setOrganizationId);
  }, []);
  useEffect(() => {
    if (!match) return;
    (async () => {
      const cached = await offlineStore.getMatchPackage(match.matchId);
      if (cached) setMatchPackage(cached);
      if (online && auth.configured)
        try {
          const value = await memberApi.matchPackage(match.matchId);
          setMatchPackage(value);
          await offlineStore.saveMatchPackage(value);
        } catch (error) {
          setStatus(error.message);
        }
    })();
  }, [match?.matchId, online]);
  useEffect(() => {
    if (!match || !online || !auth.configured || entryMode) return;
    const timer = setInterval(async () => {
      try {
        const value = await memberApi.matchPackage(match.matchId);
        setMatchPackage(value);
        await offlineStore.saveMatchPackage(value);
      } catch {
        // The cached match package remains usable while connectivity recovers.
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [match?.matchId, online, entryMode]);
  useEffect(() => {
    if (!showCurrentMatchStatus) {
      setLiveMatch(null);
      setLiveMatchUnavailable(false);
      return;
    }
    if (!match || !online || !auth.configured) return;
    let active = true;
    const refresh = async () => {
      try {
        const value = await memberApi.matchLiveStatus(match.matchId);
        if (active) {
          setLiveMatch(value);
          setLiveMatchUnavailable(false);
        }
      } catch {
        if (active) setLiveMatchUnavailable(true);
      }
    };
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [match?.matchId, online, showCurrentMatchStatus]);
  useEffect(() => {
    if (!match || !online || !auth.configured || !matchPackage?.canOpenScoreTools) return;
    let active = true;
    const refresh = async () => {
      try {
        const value = await memberApi.scoreboardGames(match.matchId);
        if (active) setScoreboardGames(value);
      } catch {
        // Match package status still controls entry availability if live board status is temporarily unavailable.
      }
    };
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [match?.matchId, online, matchPackage?.canOpenScoreTools]);
  if (!match)
    return (
      <EmptyState
        icon={<Target />}
        title="No match scheduled"
        text="Your next match will appear here after the league publishes its schedule."
      />
    );
  if (!matchPackage)
    return (
      <section className="empty-state">
        <div>
          <Download />
        </div>
        <h2>{online ? "Preparing match" : "Not downloaded"}</h2>
        <p>
          {status ||
            (online
              ? "Gathering the rules, rosters, and venue for offline use."
              : "Connect before match night to download this match package.")}
        </p>
      </section>
    );
  const memberTeam = matchPackage.teams.find((team) =>
    teams.some((value) => value.registrationId === team.registrationId),
  );
  const opponent = matchPackage.teams.find(
    (team) => team.registrationId !== memberTeam?.registrationId,
  );
  const canManageLineup =
    memberTeam?.canManageLineup ||
    teams.find((team) => team.registrationId === memberTeam?.registrationId)
      ?.isCaptain;
  const lineupsConfirmed =
    matchPackage.teams.length === 2 &&
    matchPackage.teams.every((team) => team.lineup?.status === "Confirmed");
  const liveMatchDisplay = showCurrentMatchStatus
    ? liveMatch || {
        status: online
          ? liveMatchUnavailable
            ? "Unavailable"
            : "Loading"
          : "Offline",
        homeWins: null,
        awayWins: null,
        activeGameNumbers: [],
        viewerNextGameNumber: null,
        viewerNextGameName: null,
        games: [],
      }
    : null;
  const liveCopy = showCurrentMatchStatus
    ? matchLiveCopy(liveMatchDisplay, Boolean(memberTeam?.isHome))
    : null;
  if (entryMode === "scoresheet")
    return (
      <MatchScoresheetEntry
        matchPackage={matchPackage}
        online={online}
        onBack={() => setEntryMode(null)}
      />
    );
  return (
    <section className="section-stack">
      <div className="section-heading">
        <span className="kicker">
          ROUND {matchPackage.roundNumber} ·{" "}
          {online ? "SYNCED" : "OFFLINE COPY"}
        </span>
        <h1>
          {memberTeam?.name} <em>vs</em> {opponent?.name}
        </h1>
      </div>
      {liveCopy && (
        <article className="match-live-card" data-status={liveMatchDisplay.status} aria-live="polite">
          <header>
            <span className="kicker">{liveMatchDisplay.status === "Active" ? "LIVE MATCH" : "MATCH PROGRESS"}</span>
            <b>{liveCopy.activeText}</b>
          </header>
          <div className="match-live-records">
            <span><b>{memberTeam?.name}</b><strong>{liveMatch ? `${liveCopy.memberWins} W · ${liveCopy.opponentWins} L` : "—"}</strong></span>
            <span><b>{opponent?.name}</b><strong>{liveMatch ? `${liveCopy.opponentWins} W · ${liveCopy.memberWins} L` : "—"}</strong></span>
          </div>
          <div className="match-live-next">
            <Radio />
            <span>
              <b>{liveCopy.nextTiming}</b>
              {liveMatchDisplay.viewerNextGameNumber && (
                <small>Game {liveMatchDisplay.viewerNextGameNumber} · {liveMatchDisplay.viewerNextGameName}</small>
              )}
            </span>
          </div>
        </article>
      )}
      <article className="readiness-card">
        <ShieldCheck />
        <div>
          <b>{lineupsConfirmed ? "Both lineups confirmed" : "Lineups first"}</b>
          <span>
            {lineupsConfirmed
              ? "Score entry is ready."
              : memberTeam?.lineup?.status === "Confirmed"
                ? "Your lineup is confirmed. Waiting for the other captain before score entry opens."
                : "Confirm your players for tonight. Score entry opens after both teams confirm."}
          </span>
        </div>
      </article>
      {status && <p className="sync-notice">{status}</p>}
      {lineupsConfirmed && matchPackage.canOpenScoreTools && <section className="match-entry-options">
        <span className="kicker">MATCH NIGHT ENTRY</span>
        <p>
          Open the league scoreboard on any signed-in board. Each completed
          team turn flows into this match&apos;s live scoresheet automatically.
        </p>
        <button
          className="start-match"
          disabled={!online}
          onClick={() => {
            const url = scoreboardLaunchUrl(scoreboardBaseUrl, match.matchId, organizationId);
            const opened = globalThis.open(url, "_blank");
            if (opened) opened.opener = null;
            else setStatus("Allow pop-ups to open the league scoreboard.");
          }}
        >
          <Radio />
          <span>
            <b>Open scoreboard</b>
            <small>{online ? "Choose and score a game on this match" : "Connect to open a scoring board"}</small>
          </span>
          <ChevronRight />
        </button>
        {scoreboardGames?.games?.length > 0 && (
          <div className="scoreboard-live-status" aria-live="polite">
            <span className="kicker">LIVE BOARD STATUS</span>
            {scoreboardGames.games.map((game) => (
              <div key={game.gameNumber} data-status={game.status}>
                <b>Game {game.gameNumber} · {game.name}</b>
                <span>{scoreboardStatusLabel(game)}</span>
              </div>
            ))}
          </div>
        )}
        {scoresheetV2Enabled && (
          <button
            className="start-match scoresheet-start"
            onClick={() => setEntryMode("scoresheet")}
          >
            <Pencil />
            <span>
              <b>Scoresheet entry</b>
              <small>Capture DartConnect results and corrections</small>
            </span>
            <ChevronRight />
          </button>
        )}
      </section>}
      {matchPackage.teams.map((team) => (
        <article className="detail-card" key={team.registrationId}>
          <header>
            <Users />
            <h2>{team.name}</h2>
            <strong>
              {team.lineup?.status === "Confirmed"
                ? "Confirmed"
                : `${team.isHome ? "Home" : "Away"} · waiting`}
            </strong>
          </header>
          {team.roster.map((player) => (
            <div className="detail-row" key={player.membershipId}>
              <span>
                <b>
                  {player.firstName} {player.lastName}
                </b>
                <small>{player.status}</small>
              </span>
              {player.isCaptain && <strong>Captain</strong>}
            </div>
          ))}
          {lineupsConfirmed && matchPackage.canChangeConfirmedLineups && (
            <button
              className="lineup-change-button"
              onClick={() => {
                setStatus("");
                setEditingLineupId(team.registrationId);
              }}
            >
              Change unplayed games
            </button>
          )}
        </article>
      ))}
      {canManageLineup && memberTeam?.lineup?.status !== "Confirmed" && (
        <CaptainLineup
          key={memberTeam.registrationId}
          matchPackage={matchPackage}
          team={memberTeam}
          onError={(message) => setStatus(message)}
          onSaved={async (value) => {
            const updated = {
              ...matchPackage,
              teams: matchPackage.teams.map((team) =>
                team.registrationId === value.teamRegistrationId
                  ? { ...team, lineup: value }
                  : team,
              ),
            };
            setMatchPackage(updated);
            await offlineStore.saveMatchPackage(updated);
            setStatus(value.message || "Your lineup is confirmed. Waiting for the other captain.");
          }}
        />
      )}
      {editingLineupId && (
        <CaptainLineup
          key={`change-${editingLineupId}`}
          matchPackage={matchPackage}
          team={matchPackage.teams.find((team) => team.registrationId === editingLineupId)}
          amendment
          playedGameNumbers={matchPackage.playedGameNumbers || []}
          onCancel={() => setEditingLineupId(null)}
          onError={(message) => setStatus(message)}
          onSaved={async (value) => {
            const updated = {
              ...matchPackage,
              teams: matchPackage.teams.map((team) =>
                team.registrationId === value.teamRegistrationId ? { ...team, lineup: value } : team,
              ),
            };
            setMatchPackage(updated);
            await offlineStore.saveMatchPackage(updated);
            setEditingLineupId(null);
            setStatus(value.message || "Lineup change saved and match captains were notified.");
          }}
        />
      )}
      <article className="paper-card">
        <Target />
        <div>
          <b>The show must go on</b>
          <p>
            If phones, internet, or power fail, play on paper. Photograph the
            signed scorecard here when service returns.
          </p>
          <label>
            {online ? "Photograph scorecard" : "Save photo on this phone"}
            <input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              capture="environment"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                if (online && auth.configured) {
                  await memberApi.uploadEvidence(
                    matchPackage.matchId,
                    file,
                    "Manual recovery scorecard",
                  );
                  setEvidenceNotice("Scorecard safely uploaded.");
                } else {
                  await offlineStore.saveDraft({
                    id: `evidence-${matchPackage.matchId}`,
                    matchId: matchPackage.matchId,
                    file,
                    createdAt: new Date().toISOString(),
                  });
                  setEvidenceNotice(
                    "Photo saved on this phone until reconnection.",
                  );
                }
              }}
            />
          </label>
          {evidenceNotice && <span>{evidenceNotice}</span>}
        </div>
      </article>
    </section>
  );
}

function MatchScoresheetEntry({ matchPackage, online, onBack }) {
  const [sheet, setSheet] = useState(null);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [lastLiveUpdate, setLastLiveUpdate] = useState(null);
  const home = matchPackage.teams.find((team) => team.isHome);
  const away = matchPackage.teams.find((team) => !team.isHome);

  const remember = async (value) => {
    await offlineStore.saveScoresheetDraft(matchPackage.matchId, {
      scoresheetId: value.id,
      state: value.state,
      currentGameNumber: value.currentGameNumber,
      baseVersion: value.version || 0,
      status: value.status || "Draft",
      entrySource: value.entrySource || "Manual",
      authorityType: value.authorityType,
      canEdit: value.canEdit,
      canManageAssistants: value.canManageAssistants,
      canSubmit: value.canSubmit,
      canApprove: value.canApprove,
      resultStatus: value.resultStatus || null,
      approvedRegistrationIds: value.approvedRegistrationIds || [],
      submittedAt: value.submittedAt || null,
      assistantGrants: value.assistantGrants || [],
      assistantCandidates: value.assistantCandidates || [],
      pendingEvents: value.pendingEvents || [],
      dirty: Boolean(value.dirty),
      updatedAt: new Date().toISOString(),
    });
  };

  const draftRemote = (draft) => ({
    id: draft.scoresheetId,
    matchId: matchPackage.matchId,
    version: draft.baseVersion,
    status: draft.status || "Draft",
    entrySource: draft.entrySource || "Manual",
    authorityType: draft.authorityType,
    canEdit: draft.canEdit !== false,
    canManageAssistants: draft.canManageAssistants,
    canSubmit: draft.canSubmit,
    canApprove: draft.canApprove,
    resultStatus: draft.resultStatus || null,
    approvedRegistrationIds: draft.approvedRegistrationIds || [],
    submittedAt: draft.submittedAt || null,
    assistantGrants: draft.assistantGrants || [],
    assistantCandidates: draft.assistantCandidates || [],
    state: draft.state,
  });

  const load = async (force) => {
    setLoading(true);
    setNotice("");
    const draft = await offlineStore.getScoresheetDraft(matchPackage.matchId);
    if (online && auth.configured) {
      try {
        let remote = await memberApi
          .scoresheet(matchPackage.matchId)
          .catch((error) =>
            error.status === 404
              ? memberApi.startScoresheet(matchPackage.matchId)
              : Promise.reject(error),
          );
        let hydrated = hydrateScoresheet(remote, draft, matchPackage);
        if (draft?.dirty && !hydrated.conflict && draft.pendingEvents?.length) {
          remote = await memberApi.scoresheetEvents(remote.id, {
            expectedVersion: remote.version,
            currentGameNumber: draft.currentGameNumber,
            state: draft.state,
            events: draft.pendingEvents,
          });
          hydrated = hydrateScoresheet(remote, null, matchPackage);
          setNotice("Changes saved on this phone are now synced.");
        }
        setSheet(hydrated);
        if (
          !hydrated.canEdit &&
          (hydrated.status === "Submitted" || hydrated.status === "Finalized")
        )
          setReviewing(true);
        await remember(hydrated);
        if (hydrated.conflict)
          setNotice(
            "This scoresheet changed on another phone. Your saved draft is still on this phone for review.",
          );
      } catch (error) {
        if (draft) {
          setSheet(hydrateScoresheet(draftRemote(draft), draft, matchPackage));
          setNotice(`Using the copy saved on this phone. ${error.message}`);
        } else {
          setNotice(
            error.status === 403
              ? "Only the Home Captain, an enabled Home delegate, or an invited Away Captain can enter this scoresheet."
              : error.status === 404
                ? "Scoresheet entry is not enabled for this league yet."
                : error.message,
          );
        }
      }
    } else if (draft) {
      setSheet(hydrateScoresheet(draftRemote(draft), draft, matchPackage));
      setNotice(
        "Offline copy loaded. Changes will remain on this phone until it reconnects.",
      );
    } else if (!auth.configured) {
      const local = hydrateScoresheet(
        {
          id: `local-${matchPackage.matchId}`,
          matchId: matchPackage.matchId,
          version: 0,
          status: "Draft",
          authorityType: "HomeCaptain",
          canEdit: true,
          canManageAssistants: false,
          canSubmit: true,
          canApprove: false,
          state: createScoresheetState(matchPackage),
          assistantGrants: [],
          assistantCandidates: [],
        },
        null,
        matchPackage,
      );
      setSheet(local);
      await remember(local);
    } else {
      setNotice(
        "Connect once before match night to open and store this scoresheet on your phone.",
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    load(false);
  }, [matchPackage.matchId, online]);

  useEffect(() => {
    if (!online || !auth.configured || !sheet || saving || sheet.dirty ||
        sheet.conflict || String(sheet.id).startsWith("local-")) return;
    let cancelled = false;
    const refresh = async () => {
      if (document.activeElement?.closest?.(".scoresheet-current-game")) return;
      try {
        const remote = await memberApi.scoresheet(matchPackage.matchId);
        if (cancelled || !shouldApplyScoresheetRefresh(sheet, remote)) return;
        const refreshed = hydrateScoresheet(remote, null, matchPackage);
        refreshed.currentGameNumber = sheet.currentGameNumber;
        setSheet(refreshed);
        setLastLiveUpdate(new Date());
        await remember(refreshed);
      } catch {
        // The saved copy remains usable; the regular connection state handles messaging.
      }
    };
    const timer = window.setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [matchPackage.matchId, online, saving, sheet?.id, sheet?.version,
    sheet?.status, sheet?.dirty, sheet?.conflict]);

  const chooseGame = async (gameNumber) => {
    const next = { ...sheet, currentGameNumber: gameNumber };
    setSheet(next);
    await remember(next);
  };

  const grantAssistant = async (candidate) => {
    try {
      const grant = await memberApi.grantScoresheetAssistant(
        matchPackage.matchId,
        candidate.accountId,
      );
      const next = {
        ...sheet,
        assistantGrants: [
          ...(sheet.assistantGrants || []).filter(
            (value) => value.grantedToAccountId !== candidate.accountId,
          ),
          grant,
        ],
      };
      setSheet(next);
      await remember(next);
      setNotice(
        `${candidate.firstName} ${candidate.lastName} can now help enter this match.`,
      );
    } catch (error) {
      setNotice(error.message);
    }
  };

  const revokeAssistant = async (grant) => {
    try {
      await memberApi.revokeScoresheetAssistant(matchPackage.matchId, grant.id);
      const next = {
        ...sheet,
        assistantGrants: (sheet.assistantGrants || []).filter(
          (value) => value.id !== grant.id,
        ),
      };
      setSheet(next);
      await remember(next);
      setNotice("Away Captain assistance removed.");
    } catch (error) {
      setNotice(error.message);
    }
  };

  const commitGame = async (
    updatedGame,
    eventName,
    payload,
    correctsEventId = null,
    requestedEventId = null,
  ) => {
    const shouldSync =
      online && auth.configured && !String(sheet.id).startsWith("local-");
    if (shouldSync) setSaving(true);
    const eventId = requestedEventId || crypto.randomUUID();
    const change = applyScoresheetGameChange({
      state: sheet.state,
      updatedGame,
      eventName,
      payload,
      eventId,
      correctsEventId,
      occurredAt: new Date().toISOString(),
    });
    const nextState = change.state;
    const event = change.event;
    const pendingEvents = [...(sheet.pendingEvents || []), event];
    const local = {
      ...sheet,
      state: nextState,
      pendingEvents,
      dirty: true,
      conflict: false,
    };
    setSheet(local);
    await remember(local);
    if (!shouldSync) {
      setNotice(
        "Saved on this phone. It will sync when a connection is available.",
      );
      return;
    }

    try {
      const remote = await memberApi.scoresheetEvents(sheet.id, {
        expectedVersion: sheet.version,
        currentGameNumber: sheet.currentGameNumber,
        state: nextState,
        events: pendingEvents,
      });
      const synced = hydrateScoresheet(remote, null, matchPackage);
      setSheet(synced);
      await remember(synced);
      setNotice("Scoresheet saved.");
    } catch (error) {
      const conflicted = { ...local, conflict: error.status === 409 };
      setSheet(conflicted);
      await remember(conflicted);
      setNotice(
        error.status === 409
          ? "Another phone changed this scoresheet. Choose which copy to keep before entering more results."
          : `Saved on this phone. ${error.message}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const useServerCopy = async () => {
    await offlineStore.clearScoresheetDraft(matchPackage.matchId);
    setSheet(null);
    await load(true);
  };

  const keepPhoneCopy = async () => {
    if (!online || !sheet?.pendingEvents?.length) return;
    setSaving(true);
    try {
      const remote = await memberApi.scoresheet(matchPackage.matchId);
      const resolved = await memberApi.scoresheetEvents(remote.id, {
        expectedVersion: remote.version,
        currentGameNumber: sheet.currentGameNumber,
        state: sheet.state,
        events: sheet.pendingEvents,
      });
      const synced = hydrateScoresheet(resolved, null, matchPackage);
      setSheet(synced);
      await remember(synced);
      setNotice(
        "This phone's corrections were saved with the complete audit history.",
      );
    } catch (error) {
      setNotice(error.message);
    } finally {
      setSaving(false);
    }
  };

  const submitScoresheet = async () => {
    if (!online || sheet.dirty || sheet.conflict || !sheet.canSubmit) return;
    setSaving(true);
    try {
      const remote = await memberApi.submitScoresheet(sheet.id, sheet.version);
      const submitted = hydrateScoresheet(remote, null, matchPackage);
      setSheet(submitted);
      await remember(submitted);
      setNotice(
        "Scoresheet submitted to the league. You can still correct it if something is found later.",
      );
    } catch (error) {
      setNotice(error.message);
      if (error.status === 409) {
        const conflicted = { ...sheet, conflict: true };
        setSheet(conflicted);
        await remember(conflicted);
      }
    } finally {
      setSaving(false);
    }
  };

  const approveScoresheet = async () => {
    if (!online || !sheet.canApprove || !away?.registrationId) return;
    setSaving(true);
    try {
      await memberApi.approveMatch(matchPackage.matchId, away.registrationId);
      const remote = await memberApi.scoresheet(matchPackage.matchId);
      const approved = hydrateScoresheet(remote, null, matchPackage);
      setSheet(approved);
      await remember(approved);
      setNotice("Match result approved. The scoresheet is now final.");
    } catch (error) {
      setNotice(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="launch-screen">
        <b>OPENING MATCH SCORESHEET</b>
      </div>
    );
  if (!sheet)
    return (
      <section className="scoresheet-shell unavailable">
        <header>
          <button onClick={onBack}>‹ Match</button>
          <span>UNAVAILABLE</span>
        </header>
        <EmptyState
          icon={<Pencil />}
          title="Scoresheet unavailable"
          text={notice}
        />
      </section>
    );
  const games = sheet.state?.games || [];
  const activeIndex = Math.max(
    0,
    games.findIndex(
      (game) => Number(game.gameNumber) === Number(sheet.currentGameNumber),
    ),
  );
  const current = games[activeIndex] || games[0];
  const grant = (sheet.assistantGrants || [])[0];
  const candidate =
    (sheet.assistantCandidates || []).find(
      (value) => !grant || value.accountId === grant.grantedToAccountId,
    ) || sheet.assistantCandidates?.[0];
  const totals = matchScoresheetTotals(
    sheet.state,
    home?.registrationId,
    away?.registrationId,
  );
  const review = reviewScoresheet(
    sheet.state,
    home?.registrationId,
    away?.registrationId,
  );
  const finalized = sheet.status === "Finalized";
  return (
    <section className="scoresheet-shell">
      <header>
        <button onClick={reviewing ? () => setReviewing(false) : onBack}>
          ‹ {reviewing ? "Games" : "Match"}
        </button>
        <span>
          {saving
            ? "SAVING"
            : sheet.conflict
              ? "NEEDS REVIEW"
            : finalized
              ? "FINAL"
              : sheet.status === "Submitted"
                ? "SUBMITTED"
                : online
                  ? sheet.dirty
                    ? "SAVED LOCALLY"
                    : sheet.entrySource === "Scoreboard" || sheet.entrySource === "Mixed"
                      ? "LIVE · SYNCED"
                      : "SYNCED"
                  : "OFFLINE COPY"}
        </span>
        <button
          disabled={!online || saving}
          onClick={() => load(true)}
          aria-label="Refresh scoresheet"
        >
          <RefreshCw />
        </button>
      </header>
      <div className="scoresheet-match">
        <span className="kicker">
          LIVE SCORESHEET · {(sheet.entrySource || "Manual").toUpperCase()} ENTRY
        </span>
        <h1>
          {home?.name} <em>vs</em> {away?.name}
        </h1>
        <p>
          {sheet.authorityType === "AwayCaptainReviewer"
            ? "Reviewing as Away Captain"
            : sheet.authorityType === "AwayCaptainAssistant"
              ? "Helping the Home Captain"
              : sheet.authorityType === "HomeDelegate"
                ? "Entering as Home Team delegate"
                : "Entering as Home Captain"}
          {lastLiveUpdate && ` · Live update ${lastLiveUpdate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}
        </p>
        <div className="scoresheet-totals">
          <span>
            <b>{totals.homeWins}</b>
            <small>Home wins</small>
            <em>
              {totals.homeGamePoints} pts · {totals.homeQuickPoints} QP
            </em>
          </span>
          <i>–</i>
          <span>
            <b>{totals.awayWins}</b>
            <small>Away wins</small>
            <em>
              {totals.awayGamePoints} pts · {totals.awayQuickPoints} QP
            </em>
          </span>
        </div>
      </div>
      {notice && (
        <p className={`sync-notice ${sheet.conflict ? "conflict" : ""}`}>
          {notice}
        </p>
      )}
      {sheet.conflict && (
        <div className="scoresheet-conflict-actions">
          <button disabled={!online || saving} onClick={useServerCopy}>
            Use server copy
          </button>
          <button disabled={!online || saving} onClick={keepPhoneCopy}>
            Keep this phone
          </button>
        </div>
      )}
      {sheet.canManageAssistants && candidate && !reviewing && (
        <article className="scoresheet-assistant">
          <div>
            <ShieldCheck />
            <span>
              <b>Away Captain assistance</b>
              <small>
                {candidate.firstName} {candidate.lastName}
              </small>
            </span>
          </div>
          {grant ? (
            <button disabled={!online} onClick={() => revokeAssistant(grant)}>
              Remove access
            </button>
          ) : (
            <button
              disabled={!online}
              onClick={() => grantAssistant(candidate)}
            >
              Allow help
            </button>
          )}
        </article>
      )}
      {reviewing ? (
        <ScoresheetReview
          sheet={sheet}
          review={review}
          totals={totals}
          home={home}
          away={away}
          online={online}
          saving={saving}
          onEdit={async (gameNumber) => {
            await chooseGame(gameNumber);
            setReviewing(false);
          }}
          onSubmit={submitScoresheet}
          onApprove={approveScoresheet}
        />
      ) : (
        <>
          <nav className="scoresheet-game-strip" aria-label="Scoresheet games">
            {games.map((game) => (
              <button
                disabled={saving || sheet.conflict}
                className={
                  game.gameNumber === current.gameNumber
                    ? "active"
                    : game.status === "Complete"
                      ? "complete"
                      : ""
                }
                onClick={() => chooseGame(game.gameNumber)}
                key={game.gameNumber}
              >
                <small>{game.sectionLabel}</small>
                <b>{game.gameNumber}</b>
                <span>{game.status === "Complete" ? "Done" : "Open"}</span>
              </button>
            ))}
          </nav>
          <ScoresheetGameEditor
            key={current.gameNumber}
            game={current}
            home={home}
            away={away}
            disabled={saving || sheet.conflict || !sheet.canEdit}
            onCommit={commitGame}
          />
          <footer className="scoresheet-progress">
            <span>
              {activeIndex + 1} of {games.length} games
            </span>
            {activeIndex < games.length - 1 ? (
              <button
                disabled={saving || sheet.conflict}
                onClick={() => chooseGame(games[activeIndex + 1]?.gameNumber)}
              >
                Next game <ChevronRight />
              </button>
            ) : (
              <button
                disabled={saving || sheet.conflict}
                onClick={() => setReviewing(true)}
              >
                Review match <CheckCheck />
              </button>
            )}
          </footer>
        </>
      )}
    </section>
  );
}

function ScoresheetReview({
  sheet,
  review,
  totals,
  home,
  away,
  online,
  saving,
  onEdit,
  onSubmit,
  onApprove,
}) {
  const winnerName = (game) =>
    game.winnerRegistrationId === home?.registrationId
      ? home?.name
      : game.winnerRegistrationId === away?.registrationId
        ? away?.name
        : "Winner missing";
  const finalized = sheet.status === "Finalized";
  const readOnly = !sheet.canEdit;
  return (
    <section className="scoresheet-review">
      <header>
        <span className="kicker">FINAL REVIEW</span>
        <h2>
          {review.completedGames} of {review.gameCount} games complete
        </h2>
        <p>
          {sheet.canApprove
            ? "Review every game, point total, and QP before approving for the Away Team."
            : "Check every game winner, point total, and QP before the Home Captain submits."}
        </p>
      </header>
      {(sheet.status === "Submitted" || finalized) && (
        <div className="scoresheet-submitted">
          <ShieldCheck />
          <div>
            <b>
              {finalized ? "Final league result" : "Submitted to the league"}
            </b>
            <span>
              {sheet.submittedAt
                ? new Date(sheet.submittedAt).toLocaleString()
                : "Submission recorded"}
            </span>
            <small>
              {finalized
                ? "Both teams approved this result. League corrections now require an organizer."
                : sheet.canEdit
                  ? "The result and player summaries are posted pending Away Captain approval. A correction will withdraw them and reopen Draft."
                  : "The Home Captain approved this result. Review it for the Away Team."}
            </small>
          </div>
        </div>
      )}
      {review.errors.length > 0 && (
        <div className="scoresheet-review-errors" role="alert">
          <b>
            {review.errors.length} item{review.errors.length === 1 ? "" : "s"}{" "}
            need attention
          </b>
          {review.errors.map((error, index) => (
            <button
              key={`${error.gameNumber}-${error.field}-${index}`}
              onClick={() => error.gameNumber && onEdit(error.gameNumber)}
            >
              {error.gameNumber ? `Game ${error.gameNumber}: ` : ""}
              {error.message}
            </button>
          ))}
        </div>
      )}
      <div className="scoresheet-review-list">
        {sheet.state.games.map((game) => (
          <article key={game.gameNumber}>
            <div>
              <small>
                {game.sectionLabel} · Game {game.gameNumber}
              </small>
              <b>{game.label}</b>
              <span
                className={
                  game.status === "Complete" ? "complete" : "incomplete"
                }
              >
                {winnerName(game)}
              </span>
            </div>
            <dl>
              <div>
                <dt>Points</dt>
                <dd>
                  {game.homeGamePoints || 0}–{game.awayGamePoints || 0}
                </dd>
              </div>
              <div>
                <dt>QPs</dt>
                <dd>
                  {
                    (game.quickPoints || []).filter(
                      (value) => value.side === "home",
                    ).length
                  }
                  –
                  {
                    (game.quickPoints || []).filter(
                      (value) => value.side === "away",
                    ).length
                  }
                </dd>
              </div>
            </dl>
            <button onClick={() => onEdit(game.gameNumber)}>
              {readOnly ? "View" : "Edit"}
            </button>
          </article>
        ))}
      </div>
      <div className="scoresheet-review-total">
        <span>
          <small>{home?.name}</small>
          <b>{totals.homeWins}</b>
          <em>
            {totals.homeGamePoints} pts · {totals.homeQuickPoints} QP
          </em>
        </span>
        <i>–</i>
        <span>
          <small>{away?.name}</small>
          <b>{totals.awayWins}</b>
          <em>
            {totals.awayGamePoints} pts · {totals.awayQuickPoints} QP
          </em>
        </span>
      </div>
      {sheet.canApprove && (
        <div className="scoresheet-submit">
          <button disabled={!online || saving} onClick={onApprove}>
            Approve final result <ShieldCheck />
          </button>
          <p>
            {online
              ? "Approval finalizes the match and locks captain editing."
              : "Reconnect before approving this match."}
          </p>
        </div>
      )}
      {sheet.status !== "Submitted" && !finalized && (
        <div className="scoresheet-submit">
          <button
            disabled={
              !review.canSubmit ||
              !sheet.canSubmit ||
              !online ||
              sheet.dirty ||
              sheet.conflict ||
              saving
            }
            onClick={onSubmit}
          >
            Submit scoresheet <ShieldCheck />
          </button>
          {!sheet.canSubmit ? (
            <p>
              Only the Home Captain can submit. You can continue helping with
              entries and corrections.
            </p>
          ) : !online ? (
            <p>
              Reconnect before submitting. The complete draft remains safe on
              this phone.
            </p>
          ) : sheet.dirty ? (
            <p>Wait for saved changes to finish syncing before submitting.</p>
          ) : !review.canSubmit ? (
            <p>Correct the items above before submitting.</p>
          ) : (
            <p>
              Submission posts the result and player summaries for captain
              approval.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function ScoresheetGameEditor({ game, home, away, disabled, onCommit }) {
  const [points, setPoints] = useState({
    home: game.homeGamePoints || 0,
    away: game.awayGamePoints || 0,
  });
  const [notes, setNotes] = useState(game.notes || "");
  const [qp, setQp] = useState({
    side: "home",
    playerMembershipId: game.homePlayers?.[0]?.membershipId || "",
    entryType: "QualityPoint",
    value: "",
    quantity: "1",
    editingId: null,
  });
  const [performanceOpen, setPerformanceOpen] = useState(false);
  const homeId = home?.registrationId;
  const awayId = away?.registrationId;
  const isCricket = isCricketScoresheetGame(game);
  const gameDisplayName = game.displayName || scoresheetGameDisplayName(game);
  const players =
    qp.side === "home" ? game.homePlayers || [] : game.awayPlayers || [];
  const liveCricketTotals = Object.entries(game.liveScore?.cricket || {}).reduce(
    (totals, [key, value]) => ({
      ...totals,
      [key.startsWith("home-") ? "home" : "away"]:
        totals[key.startsWith("home-") ? "home" : "away"] + (Number(value) || 0),
    }),
    { home: 0, away: 0 },
  );
  const playerLabel = (player) =>
    `${player?.firstName || ""} ${player?.lastName || ""}`.trim();
  const playerDisplayLabel = (player) => {
    const label = playerLabel(player);
    return label.length > 15 ? `${label.slice(0, 14)}…` : label;
  };
  const openPerformanceEntry = (
    side,
    playerMembershipId,
    entryType = "QualityPoint",
  ) => {
    setQp({
      side,
      playerMembershipId,
      entryType,
      value: entryType === "MarksInTurn" ? "4" : "",
      quantity: "1",
      editingId: null,
    });
    setPerformanceOpen(true);
  };
  const teamButton = (team, label, selected, onClick) => (
    <button
      disabled={disabled}
      className={selected ? "selected" : ""}
      onClick={onClick}
    >
      {label}
    </button>
  );

  const setLegWinner = async (leg, registrationId) => {
    const winnerRegistrationId =
      leg.winnerRegistrationId === registrationId ? null : registrationId;
    const eventId = crypto.randomUUID();
    const legs = game.legs.map((value) =>
      value.legNumber === leg.legNumber
        ? { ...value, winnerRegistrationId, lastEventId: eventId }
        : value,
    );
    await onCommit(
      { ...game, legs, status: "InProgress" },
      "LegResult",
      {
        legNumber: leg.legNumber,
        winnerRegistrationId,
        previousWinnerRegistrationId: leg.winnerRegistrationId,
      },
      leg.lastEventId || null,
      eventId,
    );
  };

  const setWinner = async (registrationId) => {
    const winnerRegistrationId =
      game.winnerRegistrationId === registrationId ? null : registrationId;
    const eventId = crypto.randomUUID();
    await onCommit(
      {
        ...game,
        winnerRegistrationId,
        resultEventId: eventId,
        homeGamePoints: winnerRegistrationId === homeId ? 1 : 0,
        awayGamePoints: winnerRegistrationId === awayId ? 1 : 0,
        status: winnerRegistrationId ? "Complete" : "InProgress",
      },
      "GameResult",
      {
        winnerRegistrationId,
        previousWinnerRegistrationId: game.winnerRegistrationId,
      },
      game.resultEventId || null,
      eventId,
    );
    setPoints({
      home: winnerRegistrationId === homeId ? 1 : 0,
      away: winnerRegistrationId === awayId ? 1 : 0,
    });
  };

  const savePoints = async () => {
    const homeGamePoints = Math.max(0, Math.min(99, Number(points.home) || 0));
    const awayGamePoints = Math.max(0, Math.min(99, Number(points.away) || 0));
    const eventId = crypto.randomUUID();
    await onCommit(
      {
        ...game,
        homeGamePoints,
        awayGamePoints,
        pointsEventId: eventId,
        status: game.status === "NotEntered" ? "InProgress" : game.status,
      },
      "GamePoints",
      {
        homeGamePoints,
        awayGamePoints,
        previousHomeGamePoints: game.homeGamePoints,
        previousAwayGamePoints: game.awayGamePoints,
      },
      game.pointsEventId || null,
      eventId,
    );
  };

  const savePerformanceEntry = async (event) => {
    event.preventDefault();
    const player = players.find(
      (value) => value.membershipId === qp.playerMembershipId,
    );
    const isMarksEntry = isCricket && qp.entryType === "MarksInTurn";
    const value = isMarksEntry
      ? Math.max(4, Math.min(6, Number(qp.value) || 4))
      : Math.max(0, Math.min(180, Number(qp.value) || 0));
    const quantity = Math.max(1, Math.min(20, Number(qp.quantity) || 1));
    if (!player || value === 0) return;
    const eventId = crypto.randomUUID();
    const existing = (game.quickPoints || []).find(
      (entry) => entry.id === qp.editingId,
    );
    const performanceEntry = {
      id: existing?.id || eventId,
      eventId,
      side: qp.side,
      playerMembershipId: player.membershipId,
      playerName: playerLabel(player),
      entryType: isCricket ? qp.entryType : "QualityPoint",
      value,
      quantity,
      metric: isCricket && qp.entryType === "MarksInTurn" ? "Marks" : "Points",
    };
    const quickPoints = existing
      ? (game.quickPoints || []).map((entry) =>
          entry.id === existing.id ? performanceEntry : entry,
        )
      : [...(game.quickPoints || []), performanceEntry];
    await onCommit(
      {
        ...game,
        quickPoints,
        status: game.status === "NotEntered" ? "InProgress" : game.status,
      },
      performanceEntry.entryType,
      performanceEntry,
      existing?.eventId || existing?.id || null,
      eventId,
    );
    setQp((current) => ({
      ...current,
      value: "",
      quantity: "1",
      editingId: null,
    }));
    setPerformanceOpen(false);
  };

  const editPerformanceEntry = (entry) => {
    setQp({
      side: entry.side,
      playerMembershipId: entry.playerMembershipId,
      entryType:
        entry.entryType || (entry.metric === "Marks" ? "MarksInTurn" : "QualityPoint"),
      value: String(entry.value),
      quantity: String(entry.quantity || 1),
      editingId: entry.id,
    });
    setPerformanceOpen(true);
  };

  const removeQuickPoint = async (quickPoint) => {
    await onCommit(
      {
        ...game,
        quickPoints: (game.quickPoints || []).filter(
          (value) => value.id !== quickPoint.id,
        ),
      },
      "QuickPointRemoved",
      {
        removedQuickPointId: quickPoint.id,
        playerMembershipId: quickPoint.playerMembershipId,
        value: quickPoint.value,
        metric: quickPoint.metric,
      },
      quickPoint.eventId || quickPoint.id,
    );
  };

  const saveNotes = async () => {
    const nextNotes = notes.trim();
    if (nextNotes === (game.notes || "")) return;
    const eventId = crypto.randomUUID();
    await onCommit(
      {
        ...game,
        notes: nextNotes,
        notesEventId: eventId,
        status: game.status === "NotEntered" ? "InProgress" : game.status,
      },
      "GameNotes",
      {
        notes: nextNotes,
        previousNotes: game.notes || "",
      },
      game.notesEventId || null,
      eventId,
    );
  };

  return (
    <article className="scoresheet-current-game">
      <header>
        <div>
          <span className="kicker">
            GAME {game.gameNumber} · {game.sectionLabel}
          </span>
          <h2>{gameDisplayName}</h2>
        </div>
        <strong>
          {game.status === "Complete"
            ? "Complete"
            : game.status === "InProgress"
              ? "Editing"
              : "Not entered"}
        </strong>
      </header>
      <div className="scoresheet-players">
        <div>
          <small>HOME PLAYERS</small>
          {game.homePlayers?.length ? (
            game.homePlayers.map((player) => (
              <span className="scoresheet-player-row home" key={player.membershipId}>
                <b title={playerLabel(player)}>{playerDisplayLabel(player)}</b>
                <button
                  disabled={disabled}
                  onClick={() =>
                    openPerformanceEntry("home", player.membershipId)
                  }
                >
                  ← QP
                </button>
              </span>
            ))
          ) : (
            <span>Lineup not set</span>
          )}
        </div>
        <div>
          <small>AWAY PLAYERS</small>
          {game.awayPlayers?.length ? (
            game.awayPlayers.map((player) => (
              <span className="scoresheet-player-row away" key={player.membershipId}>
                <button
                  disabled={disabled}
                  onClick={() =>
                    openPerformanceEntry("away", player.membershipId)
                  }
                >
                  QP →
                </button>
                <b title={playerLabel(player)}>{playerDisplayLabel(player)}</b>
              </span>
            ))
          ) : (
            <span>Lineup not set</span>
          )}
        </div>
      </div>
      {game.liveScore?.source === "Scoreboard" && (
        <section className="scoresheet-live-score">
          <span className="kicker">LIVE FROM SCOREBOARD</span>
          <div>
            <strong>
              {isCricket
                ? `${liveCricketTotals.home} marks`
                : `${game.liveScore.homeRemaining ?? "—"} remaining`}
            </strong>
            <i>–</i>
            <strong>
              {isCricket
                ? `${liveCricketTotals.away} marks`
                : `${game.liveScore.awayRemaining ?? "—"} remaining`}
            </strong>
          </div>
          <small>Updates automatically as scoreboard entries are saved.</small>
        </section>
      )}
      {game.legs?.length > 1 && (
        <section className="scoresheet-legs">
          <span className="kicker">LEG WINNERS</span>
          {game.legs.map((leg) => (
            <div key={leg.legNumber}>
              <b>{gameDisplayName} · Leg {leg.legNumber}</b>
              {teamButton(
                home,
                "Home",
                leg.winnerRegistrationId === homeId,
                () => setLegWinner(leg, homeId),
              )}
              {teamButton(
                away,
                "Away",
                leg.winnerRegistrationId === awayId,
                () => setLegWinner(leg, awayId),
              )}
            </div>
          ))}
        </section>
      )}
      <section className="scoresheet-winner">
        <span className="kicker">GAME WINNER</span>
        <div>
          {teamButton(
            home,
            home?.name || "Home",
            game.winnerRegistrationId === homeId,
            () => setWinner(homeId),
          )}
          {teamButton(
            away,
            away?.name || "Away",
            game.winnerRegistrationId === awayId,
            () => setWinner(awayId),
          )}
        </div>
      </section>
      <section className="scoresheet-game-points">
        <span className="kicker">GAME POINTS</span>
        <div>
          <label>
            <small>Home</small>
            <input
              disabled={disabled}
              inputMode="numeric"
              value={points.home}
              onChange={(event) =>
                setPoints((value) => ({
                  ...value,
                  home: event.target.value.replace(/\D/g, "").slice(0, 2),
                }))
              }
            />
          </label>
          <i>–</i>
          <label>
            <small>Away</small>
            <input
              disabled={disabled}
              inputMode="numeric"
              value={points.away}
              onChange={(event) =>
                setPoints((value) => ({
                  ...value,
                  away: event.target.value.replace(/\D/g, "").slice(0, 2),
                }))
              }
            />
          </label>
          <button disabled={disabled} onClick={savePoints}>
            Save points
          </button>
        </div>
      </section>
      <section className="scoresheet-qp">
        <span className="kicker">QUALITY POINTS{isCricket ? " & CRICKET MARKS" : ""}</span>
        <p>
          Record qualifying player performances here. Entries can be corrected
          or removed before the scoresheet is finalized.
        </p>
        {(game.quickPoints || []).map((value) => (
          <div className="scoresheet-qp-item" key={value.id}>
            <span>
              <b>{value.playerName}</b>
              <small>
                {value.side} · {value.quantity > 1 ? `${value.quantity} × ` : ""}
                {value.entryType === "MarksInTurn" || (!value.entryType && value.metric === "Marks")
                  ? `${value.value}-Mark`
                  : `${value.value} Quality Points`}
              </small>
            </span>
            <div>
              <button disabled={disabled} onClick={() => editPerformanceEntry(value)}>
                Edit
              </button>
              <button disabled={disabled} onClick={() => removeQuickPoint(value)}>
                Remove
              </button>
            </div>
          </div>
        ))}
        <div className="scoresheet-performance-actions">
          <button
            disabled={disabled}
            onClick={() =>
              openPerformanceEntry(
                "home",
                game.homePlayers?.[0]?.membershipId || "",
              )
            }
          >
            Add Quality Points
          </button>
          {isCricket && (
            <button
              disabled={disabled}
              onClick={() =>
                openPerformanceEntry(
                  "home",
                  game.homePlayers?.[0]?.membershipId || "",
                  "MarksInTurn",
                )
              }
            >
              Add Cricket Marks
            </button>
          )}
        </div>
      </section>
      {performanceOpen && (
        <div
          className="scoresheet-performance-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPerformanceOpen(false);
          }}
        >
          <section
            aria-labelledby="performance-entry-title"
            aria-modal="true"
            className="scoresheet-performance-modal"
            role="dialog"
          >
            <header>
              <div>
                <span className="kicker">PLAYER PERFORMANCE</span>
                <h3 id="performance-entry-title">
                  {qp.editingId ? "Edit entry" : "Add entry"}
                </h3>
              </div>
              <button
                aria-label="Close performance entry"
                onClick={() => setPerformanceOpen(false)}
                type="button"
              >
                ×
              </button>
            </header>
            <form onSubmit={savePerformanceEntry}>
              {isCricket && (
                <label>
                  <span>Entry type</span>
                  <select
                    disabled={disabled}
                    value={qp.entryType}
                    onChange={(event) =>
                      setQp((value) => ({
                        ...value,
                        entryType: event.target.value,
                        value:
                          event.target.value === "MarksInTurn" ? "4" : "",
                      }))
                    }
                  >
                    <option value="QualityPoint">Quality Points</option>
                    <option value="MarksInTurn">Cricket Marks</option>
                  </select>
                </label>
              )}
              <label className="wide">
                <span>Team</span>
                <select
                  disabled={disabled}
                  value={qp.side}
                  onChange={(event) => {
                    const side = event.target.value;
                    const nextPlayers =
                      side === "home"
                        ? game.homePlayers || []
                        : game.awayPlayers || [];
                    setQp((current) => ({
                      ...current,
                      side,
                      playerMembershipId:
                        nextPlayers[0]?.membershipId || "",
                    }));
                  }}
                >
                  <option value="home">Home</option>
                  <option value="away">Away</option>
                </select>
              </label>
              <label className="wide">
                <span>Player</span>
                <select
                  disabled={disabled}
                  value={qp.playerMembershipId}
                  onChange={(event) =>
                    setQp((value) => ({
                      ...value,
                      playerMembershipId: event.target.value,
                    }))
                  }
                >
                  <option value="">Choose player</option>
                  {players.map((player) => (
                    <option
                      key={player.membershipId}
                      value={player.membershipId}
                    >
                      {playerLabel(player)}
                    </option>
                  ))}
                </select>
              </label>
              {qp.entryType === "MarksInTurn" ? (
                <label>
                  <span>Marks</span>
                  <select
                    disabled={disabled}
                    value={qp.value}
                    onChange={(event) =>
                      setQp((value) => ({
                        ...value,
                        value: event.target.value,
                      }))
                    }
                  >
                    <option value="4">4-Mark</option>
                    <option value="5">5-Mark</option>
                    <option value="6">6-Mark</option>
                  </select>
                </label>
              ) : (
                <label>
                  <span>Points</span>
                  <input
                    disabled={disabled}
                    inputMode="numeric"
                    placeholder="Points"
                    value={qp.value}
                    onChange={(event) =>
                      setQp((value) => ({
                        ...value,
                        value: event.target.value
                          .replace(/\D/g, "")
                          .slice(0, 3),
                      }))
                    }
                  />
                </label>
              )}
              <label>
                <span>Quantity</span>
                <input
                  disabled={disabled}
                  inputMode="numeric"
                  value={qp.quantity}
                  onChange={(event) =>
                    setQp((value) => ({
                      ...value,
                      quantity: event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 2),
                    }))
                  }
                />
              </label>
              <p className="scoresheet-performance-summary">
                {qp.playerMembershipId
                  ? `Record ${Math.max(1, Number(qp.quantity) || 1)} × ${qp.value || "—"} ${
                      qp.entryType === "MarksInTurn"
                        ? "Mark"
                        : "Quality Points"
                    } for ${playerLabel(
                      players.find(
                        (player) =>
                          player.membershipId === qp.playerMembershipId,
                      ) || {},
                    )}`
                  : "Choose a player to record this performance."}
              </p>
              <div className="scoresheet-performance-buttons">
                <button
                  onClick={() => setPerformanceOpen(false)}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  disabled={
                    disabled || !qp.playerMembershipId || !qp.value
                  }
                  type="submit"
                >
                  {qp.editingId ? "Save change" : "Save performance"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
      <section className="scoresheet-notes">
        <label>
          <span className="kicker">GAME NOTES</span>
          <textarea
            disabled={disabled}
            maxLength="500"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Optional correction or match-night note"
          />
        </label>
        <button
          disabled={disabled || notes.trim() === (game.notes || "")}
          onClick={saveNotes}
        >
          Save note
        </button>
      </section>
    </article>
  );
}

function CaptainLineup({ matchPackage, team, onSaved, onError, amendment = false, playedGameNumbers = [], onCancel }) {
  const existing = team.lineup?.assignments?.games || [];
  const formatGames = (matchPackage.matchFormat?.sections || []).flatMap(
    (section, sectionIndex) =>
      (section.games || []).map((game) => ({
        ...game,
        sectionKey: `section-${sectionIndex}`,
        sectionName:
          section.name || section.label || `Section ${sectionIndex + 1}`,
      })),
  );
  const games = existing.length
    ? existing
    : formatGames.map((game, index) => ({
        gameNumber: game.gameNumber || index + 1,
        label: game.name || game.gameType || `Game ${index + 1}`,
        playerIds: [],
      }));
  const [assignments, setAssignments] = useState({ games });
  const [saving, setSaving] = useState(false);
  const definitionFor = (game, index) =>
    formatGames.find(
      (value) => Number(value.gameNumber) === Number(game.gameNumber),
    ) || formatGames[index];
  const sectionFor = (game, index) =>
    definitionFor(game, index)?.sectionKey || `game-${index}`;
  const toggle = (gameIndex, id) =>
    setAssignments((value) => {
      const target = value.games[gameIndex];
      const targetSection = sectionFor(target, gameIndex);
      const removing = target.playerIds?.includes(id);
      return {
        games: value.games.map((game, index) => {
          const playerIds = game.playerIds || [];
          if (index === gameIndex)
            return {
              ...game,
              playerIds: removing
                ? playerIds.filter((value) => value !== id)
                : [...playerIds, id],
            };
          if (
            !removing &&
            sectionFor(game, index) === targetSection &&
            playerIds.includes(id)
          )
            return {
              ...game,
              playerIds: playerIds.filter((value) => value !== id),
            };
          return game;
        }),
      };
    });
  const save = async (confirm) => {
    setSaving(true);
    try {
      const value = auth.configured
        ? await memberApi.saveLineup(
            matchPackage.matchId,
            team.registrationId,
            assignments,
            confirm,
          )
        : {
            id: "demo",
            teamRegistrationId: team.registrationId,
            status: confirm ? "Confirmed" : "Draft",
            assignments,
            updatedAt: new Date().toISOString(),
          };
      await onSaved(value);
    } catch (error) {
      onError?.(error.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <article className="lineup-card">
      <span className="kicker">CAPTAIN TOOLS</span>
      <h2>{amendment ? `Change ${team.name} lineup` : "Set your lineup"}</h2>
      <p>
        {amendment ? "Only games that have not started can be changed. Both captains and active delegates will be notified." : "Tap players for each game. A player can appear once in each section and becomes available again in the next section."}
      </p>
      {games.length ? (
        assignments.games.map((game, index) => {
          const definition = definitionFor(game, index);
          const section = sectionFor(game, index);
          const firstInSection =
            index === 0 ||
            sectionFor(assignments.games[index - 1], index - 1) !== section;
          const availablePlayers = team.roster.filter(
            (player) =>
              game.playerIds?.includes(player.membershipId) ||
              !assignments.games.some(
                (other, otherIndex) =>
                  otherIndex !== index &&
                  sectionFor(other, otherIndex) === section &&
                  other.playerIds?.includes(player.membershipId),
              ),
          );
          const played = playedGameNumbers.includes(Number(game.gameNumber || index + 1));
          return (
            <React.Fragment key={game.gameNumber || index}>
              {firstInSection && definition?.sectionName && (
                <span className="lineup-section-label">
                  {definition.sectionName}
                </span>
              )}
              <div className="lineup-game">
                <b>
                  {game.gameNumber || index + 1}.{" "}
                  {game.label || `Game ${index + 1}`}
                </b>
                {played && <small>Already started · locked</small>}
                <div>
                  {availablePlayers.map((player) => (
                    <button
                      className={
                        game.playerIds?.includes(player.membershipId)
                          ? "selected"
                          : ""
                      }
                      key={player.membershipId}
                      disabled={played}
                      onClick={() => toggle(index, player.membershipId)}
                    >
                      {player.firstName} {player.lastName}
                    </button>
                  ))}
                </div>
              </div>
            </React.Fragment>
          );
        })
      ) : (
        <p className="muted-copy">
          This match format has no game assignments yet. The roster is still
          available offline.
        </p>
      )}
      <div className="lineup-actions">
        {amendment ? <button disabled={saving} onClick={onCancel}>Cancel</button> : <button disabled={saving} onClick={() => save(false)}>Save draft</button>}
        <button disabled={saving} onClick={() => save(true)}>
          {saving ? "Saving…" : amendment ? "Save lineup changes" : "Confirm lineup"}
        </button>
      </div>
    </article>
  );
}

const gameDefinitions = (matchPackage) => {
  const games =
    matchPackage.matchFormat?.sections?.flatMap(
      (section) => section.games || [],
    ) || [];
  return games.length
    ? games
    : [
        {
          gameNumber: 1,
          name: "301 Singles",
          gameType: "X01",
          startingScore: 301,
        },
        { gameNumber: 2, name: "Cricket Singles", gameType: "Cricket" },
      ];
};

function LiveScorer({ matchPackage, online, memberTeam, canApprove, onBack }) {
  const definitions = gameDefinitions(matchPackage);
  const initial = {
    games: definitions.map((game, index) => {
      const gameNumber = game.gameNumber || index + 1;
      const players = (side) => {
        const team = matchPackage.teams.find(
          (value) => value.isHome === (side === "home"),
        );
        const assigned =
          team?.lineup?.assignments?.games?.find(
            (value) => Number(value.gameNumber) === Number(gameNumber),
          )?.playerIds || [];
        return assigned.length
          ? team.roster.filter((player) =>
              assigned.includes(player.membershipId),
            )
          : team?.roster || [];
      };
      return {
        gameNumber,
        label: game.name || game.gameType,
        type: String(game.gameType || game.type || "X01").toLowerCase(),
        status: "Ready",
        homeScore: game.startingScore || 301,
        awayScore: game.startingScore || 301,
        cricket: {},
        homeRegistrationId: matchPackage.teams.find((team) => team.isHome)
          ?.registrationId,
        awayRegistrationId: matchPackage.teams.find((team) => !team.isHome)
          ?.registrationId,
        homePlayers: players("home"),
        awayPlayers: players("away"),
        winnerRegistrationId: null,
      };
    }),
  };
  const [state, setState] = useState(initial);
  const [session, setSession] = useState(null);
  const [active, setActive] = useState(0);
  const [history, setHistory] = useState([]);
  const [notice, setNotice] = useState("");
  useEffect(() => {
    (async () => {
      let deviceId = await offlineStore.getPreference("device-id");
      if (!deviceId) {
        deviceId = crypto.randomUUID();
        await offlineStore.setPreference("device-id", deviceId);
      }
      const local = {
        id: `local-${matchPackage.matchId}`,
        matchId: matchPackage.matchId,
        deviceId,
        version: 0,
        status: "Active",
      };
      if (online && auth.configured)
        try {
          const remote = await memberApi.startScoring(
            matchPackage.matchId,
            deviceId,
          );
          setSession({ ...remote, deviceId });
          if (remote.state?.games?.length) setState(remote.state);
        } catch (error) {
          setNotice(error.message);
          setSession(local);
        }
      else setSession(local);
    })();
  }, []);
  useEffect(() => {
    if (!online || !auth.configured || !session?.id.startsWith("local-"))
      return;
    (async () => {
      try {
        const remote = await memberApi.startScoring(
          matchPackage.matchId,
          session.deviceId,
        );
        const pending = (
          await offlineStore.getMatchEvents(matchPackage.matchId)
        )
          .filter((event) => event.syncState === "pending")
          .map(({ id, eventType, payload, occurredAt }) => ({
            id,
            eventType,
            payload,
            occurredAt,
          }));
        const synced = pending.length
          ? await memberApi.scoreEvents(remote.id, {
              deviceId: session.deviceId,
              expectedVersion: remote.version,
              state,
              events: pending,
            })
          : remote;
        for (const event of await offlineStore.getMatchEvents(
          matchPackage.matchId,
        ))
          if (event.syncState === "pending")
            await offlineStore.saveEvent({ ...event, syncState: "synced" });
        setSession({ ...synced, deviceId: session.deviceId });
        setNotice("Offline scoring synced.");
      } catch (error) {
        setNotice("Still safe on this phone. " + error.message);
      }
    })();
  }, [online, session?.id]);
  const record = async (eventType, nextState, payload, remember = true) => {
    const event = {
      id: crypto.randomUUID(),
      eventType,
      payload,
      occurredAt: new Date().toISOString(),
    };
    if (remember)
      setHistory((value) => [...value, { state, eventId: event.id }]);
    setState(nextState);
    await offlineStore.saveEvent({
      ...event,
      matchId: matchPackage.matchId,
      syncState: online ? "syncing" : "pending",
    });
    if (
      session &&
      online &&
      auth.configured &&
      !session.id.startsWith("local-")
    )
      try {
        const remote = await memberApi.scoreEvents(session.id, {
          deviceId: session.deviceId,
          expectedVersion: session.version,
          state: nextState,
          events: [event],
        });
        setSession({ ...remote, deviceId: session.deviceId });
      } catch (error) {
        setNotice("Saved on this phone. " + error.message);
      }
  };
  const updateGame = (
    patch,
    eventType = "ScoreEntered",
    eventPayload = null,
  ) => {
    const playerTurn = patch.playerTurn;
    const statePatch = playerTurn
      ? Object.fromEntries(
          Object.entries(patch).filter(([key]) => key !== "playerTurn"),
        )
      : patch;
    const next = {
      ...state,
      games: state.games.map((game, index) =>
        index === active
          ? { ...game, ...statePatch, status: "InProgress" }
          : game,
      ),
    };
    const payload = playerTurn
      ? { gameNumber: state.games[active].gameNumber, ...playerTurn }
      : eventPayload
        ? { gameNumber: state.games[active].gameNumber, ...eventPayload }
        : { gameNumber: state.games[active].gameNumber, ...statePatch };
    const recordedType = playerTurn ? "PlayerTurnRecorded" : eventType;
    if (recordedType === "PlayerTurnRecorded") {
      const earned = (matchPackage.statPolicies || []).filter((policy) => {
        if (policy.gameType !== payload.gameType) return false;
        const value =
          policy.metric === "MarksInTurn" ? payload.marks : payload.points;
        return policy.comparison === "Equals"
          ? value === policy.threshold
          : policy.comparison === "AtMost"
            ? value <= policy.threshold
            : value >= policy.threshold;
      });
      if (earned.length)
        setNotice(
          `${earned
            .map((policy) => {
              const value =
                policy.metric === "MarksInTurn"
                  ? payload.marks
                  : payload.points;
              const award =
                policy.awardMode === "ActualValue" ? value : policy.awardValue;
              return `${award} ${policy.shortLabel}`;
            })
            .join(" + ")} earned by ${payload.playerName}.`,
        );
    }
    record(recordedType, next, payload);
  };
  const undo = () => {
    const previous = history.at(-1);
    if (!previous) return;
    setHistory((value) => value.slice(0, -1));
    record(
      "Correction",
      previous.state,
      { reason: "Undo last entry", reversesEventId: previous.eventId },
      false,
    );
  };
  const current = state.games[active];
  if (!session)
    return (
      <div className="launch-screen">
        <b>OPENING SCORECARD</b>
      </div>
    );
  return (
    <section className="scorer">
      <header>
        <button onClick={onBack}>‹ Match</button>
        <span>{online ? "SYNCED" : "OFFLINE"}</span>
        <button disabled={!history.length} onClick={undo}>
          Undo
        </button>
      </header>
      <button
        className="handoff-link"
        disabled={!online || session.id.startsWith("local-")}
        onClick={async () => {
          const newDeviceId = prompt(
            `Enter the new phone's handoff code. This phone is ${session.deviceId}`,
          );
          if (!newDeviceId) return;
          const result = await memberApi.handoffScoring(session.id, {
            currentDeviceId: session.deviceId,
            newDeviceId,
            expectedVersion: session.version,
          });
          setSession({ ...result, deviceId: session.deviceId });
          setNotice("Scoring control transferred to the new phone.");
        }}
      >
        Hand off to another phone
      </button>
      <div className="match-score">
        <div>
          <small>{matchPackage.teams.find((team) => team.isHome)?.name}</small>
          <b>
            {
              state.games.filter(
                (game) =>
                  game.winnerRegistrationId ===
                  matchPackage.teams.find((team) => team.isHome)
                    ?.registrationId,
              ).length
            }
          </b>
        </div>
        <i>–</i>
        <div>
          <small>{matchPackage.teams.find((team) => !team.isHome)?.name}</small>
          <b>
            {
              state.games.filter(
                (game) =>
                  game.winnerRegistrationId ===
                  matchPackage.teams.find((team) => !team.isHome)
                    ?.registrationId,
              ).length
            }
          </b>
        </div>
      </div>
      {notice && <p className="sync-notice">{notice}</p>}
      <nav className="game-tabs">
        {state.games.map((game, index) => (
          <button
            className={index === active ? "active" : ""}
            onClick={() => setActive(index)}
            key={game.gameNumber}
          >
            {game.gameNumber}
          </button>
        ))}
      </nav>
      <div className="game-title">
        <span className="kicker">GAME {current.gameNumber}</span>
        <h1>{current.label}</h1>
      </div>
      {current.type.includes("chicago") && !current.chicagoChoice ? (
        <div className="chicago-choice">
          <p>Bull winner chooses the deciding leg.</p>
          <button
            onClick={() =>
              updateGame({ chicagoChoice: "x01" }, "ChicagoChoice")
            }
          >
            Play X01
          </button>
          <button
            onClick={() =>
              updateGame({ chicagoChoice: "cricket" }, "ChicagoChoice")
            }
          >
            Play Cricket
          </button>
        </div>
      ) : current.type.includes("cricket") ||
        current.chicagoChoice === "cricket" ? (
        <CricketScore
          game={current}
          teams={matchPackage.teams}
          onChange={(patch) => updateGame(patch, "CricketMark")}
        />
      ) : (
        <X01Score
          game={current}
          onChange={(patch) => updateGame(patch, "X01Throw")}
        />
      )}
      <div className="winner-buttons">
        <span>Game winner</span>
        {matchPackage.teams.map((team) => (
          <button
            className={
              current.winnerRegistrationId === team.registrationId
                ? "selected"
                : ""
            }
            onClick={() =>
              updateGame(
                {
                  winnerRegistrationId: team.registrationId,
                  status: "Complete",
                },
                "GameCompleted",
              )
            }
            key={team.registrationId}
          >
            {team.name}
          </button>
        ))}
      </div>
      {session.status === "Completed" && memberTeam && canApprove ? (
        <button
          className="next-game"
          onClick={async () => {
            await memberApi.approveMatch(
              matchPackage.matchId,
              memberTeam.registrationId,
            );
            setNotice("Your team has approved the final scorecard.");
          }}
        >
          Approve scorecard <ShieldCheck />
        </button>
      ) : active < state.games.length - 1 ? (
        <button className="next-game" onClick={() => setActive(active + 1)}>
          Next game <ChevronRight />
        </button>
      ) : (
        <button
          className="next-game"
          onClick={async () => {
            if (online && auth.configured && !session.id.startsWith("local-")) {
              const result = await memberApi.completeScoring(session.id, {
                deviceId: session.deviceId,
                expectedVersion: session.version,
                state,
              });
              setSession({ ...result, deviceId: session.deviceId });
            } else
              setNotice(
                "Match completion saved on this phone until you reconnect.",
              );
          }}
        >
          Complete match <ShieldCheck />
        </button>
      )}
    </section>
  );
}

function X01Score({ game, onChange }) {
  const [value, setValue] = useState("");
  const [players, setPlayers] = useState({
    home: game.homePlayers?.[0]?.membershipId || "",
    away: game.awayPlayers?.[0]?.membershipId || "",
  });
  const submit = (side) => {
    const points = Math.max(0, Math.min(180, Number(value) || 0));
    const player = game[`${side}Players`]?.find(
      (item) => item.membershipId === players[side],
    );
    if (!player) return;
    onChange({
      [`${side}Score`]: Math.max(0, game[`${side}Score`] - points),
      lastThrow: { side, points, playerMembershipId: player.membershipId },
      playerTurn: {
        gameType: "X01",
        teamRegistrationId: game[`${side}RegistrationId`],
        playerMembershipId: player.membershipId,
        playerName: `${player.firstName} ${player.lastName}`,
        points,
      },
    });
    setValue("");
  };
  const chooser = (side) => (
    <select
      aria-label={`${side} thrower`}
      value={players[side]}
      onChange={(event) =>
        setPlayers((current) => ({ ...current, [side]: event.target.value }))
      }
    >
      <option value="">Select thrower</option>
      {(game[`${side}Players`] || []).map((player) => (
        <option key={player.membershipId} value={player.membershipId}>
          {player.firstName} {player.lastName}
        </option>
      ))}
    </select>
  );
  return (
    <div className="x01-board">
      <div>
        <small>HOME</small>
        <b>{game.homeScore}</b>
        {chooser("home")}
        <button disabled={!players.home} onClick={() => submit("home")}>
          Enter throw
        </button>
      </div>
      <label>
        <span>THROW</span>
        <input
          inputMode="numeric"
          maxLength="3"
          value={value}
          onChange={(event) => setValue(event.target.value.replace(/\D/g, ""))}
          placeholder="0"
        />
      </label>
      <div>
        <small>AWAY</small>
        <b>{game.awayScore}</b>
        {chooser("away")}
        <button disabled={!players.away} onClick={() => submit("away")}>
          Enter throw
        </button>
      </div>
    </div>
  );
}
function CricketScore({ game, teams, onChange }) {
  const targets = [20, 19, 18, 17, 16, 15, "B"];
  const marks = game.cricket || {};
  const [turn, setTurn] = useState({
    side: "home",
    playerId: game.homePlayers?.[0]?.membershipId || "",
    marks: "",
  });
  const mark = (side, target) => {
    const key = `${side}-${target}`;
    onChange({ cricket: { ...marks, [key]: ((marks[key] || 0) + 1) % 4 } });
  };
  const turnPlayers = game[`${turn.side}Players`] || [];
  const saveTurn = () => {
    const player = turnPlayers.find(
      (value) => value.membershipId === turn.playerId,
    );
    if (!player) return;
    const markCount = Math.max(0, Math.min(9, Number(turn.marks) || 0));
    onChange({
      playerTurn: {
        gameType: "Cricket",
        teamRegistrationId: game[`${turn.side}RegistrationId`],
        playerMembershipId: player.membershipId,
        playerName: `${player.firstName} ${player.lastName}`,
        marks: markCount,
      },
    });
    setTurn((current) => ({ ...current, marks: "" }));
  };
  return (
    <>
      <div className="cricket-board">
        <b>{teams.find((team) => team.isHome)?.name}</b>
        <span>Target</span>
        <b>{teams.find((team) => !team.isHome)?.name}</b>
        {targets.map((target) => (
          <React.Fragment key={target}>
            <button onClick={() => mark("home", target)}>
              {"●".repeat(marks[`home-${target}`] || 0) || "—"}
            </button>
            <strong>{target}</strong>
            <button onClick={() => mark("away", target)}>
              {"●".repeat(marks[`away-${target}`] || 0) || "—"}
            </button>
          </React.Fragment>
        ))}
      </div>
      <div className="cricket-turn-entry">
        <span className="kicker">COMPLETED TURN</span>
        <p>
          Record total marks from this player's three darts so season stat rules
          can be applied.
        </p>
        <select
          value={turn.side}
          onChange={(event) => {
            const side = event.target.value;
            setTurn({
              side,
              playerId: game[`${side}Players`]?.[0]?.membershipId || "",
              marks: "",
            });
          }}
        >
          <option value="home">Home team</option>
          <option value="away">Away team</option>
        </select>
        <select
          value={turn.playerId}
          onChange={(event) =>
            setTurn((current) => ({ ...current, playerId: event.target.value }))
          }
        >
          <option value="">Select thrower</option>
          {turnPlayers.map((player) => (
            <option key={player.membershipId} value={player.membershipId}>
              {player.firstName} {player.lastName}
            </option>
          ))}
        </select>
        <input
          aria-label="Marks this turn"
          inputMode="numeric"
          maxLength="1"
          placeholder="Marks 0–9"
          value={turn.marks}
          onChange={(event) =>
            setTurn((current) => ({
              ...current,
              marks: event.target.value.replace(/\D/g, "").slice(0, 1),
            }))
          }
        />
        <button
          disabled={!turn.playerId || turn.marks === ""}
          onClick={saveTurn}
        >
          Save completed turn
        </button>
      </div>
    </>
  );
}

function Shell({
  account,
  organizationName,
  online,
  onSwitchOrganization,
  install,
}) {
  const [section, setSection] = useState("home");
  const [perspective, setPerspective] = useState("player");
  const [dashboard, setDashboard] = useState(null);
  const [notice, setNotice] = useState("");
  const [membershipMissing, setMembershipMissing] = useState(false);
  const [captainTeams, setCaptainTeams] = useState([]);
  const [captainLoadError, setCaptainLoadError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [tournamentRegistrations, setTournamentRegistrations] = useState([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const organizationId = await offlineStore.getPreference("organizationId");
      const cacheKey = `member-dashboard:v2:${organizationId || "demo"}`;
      const cached = await offlineStore.getPreference(cacheKey);
      if (cached) setDashboard(cached);
      let ownedTeams = [];
      if (auth.configured)
        try {
          ownedTeams = await memberApi.captainTeams();
        } catch (error) {
          if ((error.status || 0) >= 500)
            setCaptainLoadError(
              "Team Captain tools could not load. Try again in a moment.",
            );
        }
      setCaptainTeams(ownedTeams);
      try {
        const value = auth.configured
          ? await memberApi.dashboard()
          : demoDashboard;
        setDashboard(value);
        await offlineStore.setPreference(cacheKey, value);
      } catch (error) {
        if (error.status === 404) {
          setDashboard(null);
          setMembershipMissing(ownedTeams.length === 0);
          if (ownedTeams.length) setPerspective("team");
        } else if (!cached) setNotice(error.message);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);
  useEffect(() => {
    if (!auth.configured || !online) return;
    const refresh = () =>
      memberApi
        .notifications(true)
        .then((value) => setUnreadNotifications(value.unreadCount))
        .catch(() => {});
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => clearInterval(timer);
  }, [online]);
  useEffect(() => {
    if (!auth.configured) {
      setTournamentsLoading(false);
      return;
    }
    memberApi
      .tournamentRegistrations()
      .then(setTournamentRegistrations)
      .catch(() => {})
      .finally(() => setTournamentsLoading(false));
  }, []);
  if (perspective === "team" && captainTeams.length)
    return (
      <>
        <BrowserInstallRecommendation install={install} />
        <CaptainWorkspace
          teams={captainTeams}
          organizationName={organizationName || dashboard?.organizationName}
          online={online}
          onPlayerView={() => {
            setPerspective("player");
            setSection("home");
          }}
          onSwitch={onSwitchOrganization}
        />
      </>
    );
  if (membershipMissing)
    return (
      <>
        <BrowserInstallRecommendation install={install} />
        <MembershipRequired onSwitch={onSwitchOrganization} />
      </>
    );
  if (!dashboard)
    return (
      <>
        <BrowserInstallRecommendation install={install} />
        <div className="launch-screen">
          <span className="target-mark">◎</span>
          <b>
            {loaded
              ? notice || "YOUR PLAYER PROFILE IS NOT CONNECTED"
              : notice || "LOADING YOUR DARTS"}
          </b>
          {captainTeams.length > 0 && (
            <button
              className="action-primary"
              onClick={() => setPerspective("team")}
            >
              Open Team Dashboard
            </button>
          )}
        </div>
      </>
    );
  const nextMatch =
    dashboard.matches.find((match) => !match.isBye) || dashboard.matches[0];
  const primaryTeam = dashboard.teams[0];
  const pendingRegistrations = captainTeams.reduce(
    (sum, team) => sum + (team.pendingRegistrationCount || 0),
    0,
  );
  return (
    <>
      <BrowserInstallRecommendation install={install} />
      <div className="mobile-shell">
        <LeagueIdentityHeader
          organizationName={dashboard.organizationName || organizationName}
          leagueName={dashboard.leagueName || primaryTeam?.leagueName}
          online={online}
        />
        {captainTeams.length > 0 && (
          <PerspectiveSwitch
            perspective="player"
            captainTeams={captainTeams}
            onChange={setPerspective}
          />
        )}
        <main
          className={`app-content ${captainTeams.length > 0 ? "with-perspective" : ""}`}
        >
          {section === "home" && (
            <>
              <div className="greeting">
                <span>PLAYER DASHBOARD</span>
                <h1>
                  {dashboard.profile.firstName} {dashboard.profile.lastName}
                </h1>
                <p>
                  {primaryTeam
                    ? `${primaryTeam.teamName} · ${primaryTeam.seasonName} · ${primaryTeam.flightName}`
                    : "Your match-night home is ready."}
                </p>
              </div>
              {captainLoadError && (
                <button
                  className="captain-load-error"
                  onClick={() => location.reload()}
                >
                  <span>
                    <b>Captain tools unavailable</b>
                    <small>{captainLoadError}</small>
                  </span>
                  <span>Retry</span>
                </button>
              )}
              {pendingRegistrations > 0 && (
                <button
                  className="captain-alert"
                  onClick={() => setPerspective("team")}
                >
                  <div>
                    <span className="kicker">CAPTAIN TASK</span>
                    <b>
                      Finish{" "}
                      {pendingRegistrations === 1
                        ? "team registration"
                        : `${pendingRegistrations} team registrations`}
                    </b>
                    <small>
                      Add the remaining players and submit your roster.
                    </small>
                  </div>
                  <ChevronRight />
                </button>
              )}
              {tournamentRegistrations.some(
                (value) => !["Withdrawn", "Rejected"].includes(value.status),
              ) && (
                <button
                  className="tournament-alert"
                  onClick={() => setSection("tournaments")}
                >
                  <Trophy />
                  <span>
                    <small>TOURNAMENTS</small>
                    <b>
                      {
                        tournamentRegistrations.filter(
                          (value) =>
                            !["Withdrawn", "Rejected"].includes(value.status),
                        ).length
                      }{" "}
                      upcoming registration
                      {tournamentRegistrations.filter(
                        (value) =>
                          !["Withdrawn", "Rejected"].includes(value.status),
                      ).length === 1
                        ? ""
                        : "s"}
                    </b>
                  </span>
                  <ChevronRight />
                </button>
              )}
              <button
                className="hero-match"
                onClick={() => setSection("match")}
              >
                <span className="kicker">NEXT MATCH</span>
                <h2>
                  {nextMatch
                    ? nextMatch.isBye
                      ? "Bye week"
                      : `${nextMatch.isHome ? "vs" : "at"} ${nextMatch.opponentName}`
                    : "Schedule coming soon"}
                </h2>
                <p>
                  {nextMatch
                    ? `${dateLabel(nextMatch.matchDate)} · ${nextMatch.pubName || "Venue to be announced"}`
                    : "Your league organizer has not published the next round yet."}
                </p>
                <span className="hero-link">
                  Open match night <ChevronRight size={18} />
                </span>
              </button>
              <section className="quick-grid">
                <button onClick={() => setSection("schedule")}>
                  <CalendarDays />
                  <b>Schedule</b>
                  <span>{dashboard.matches.length} upcoming</span>
                </button>
                <button onClick={() => setSection("tournaments")}>
                  <Trophy />
                  <b>Tournaments</b>
                  <span>
                    {tournamentRegistrations.length
                      ? `${tournamentRegistrations.length} registrations`
                      : "Your events"}
                  </span>
                </button>
              </section>
            </>
          )}
          {section === "match" && (
            <MatchPrepView
              match={nextMatch}
              teams={dashboard.teams}
              online={online}
              showCurrentMatchStatus
            />
          )}
          {section === "schedule" && (
            <ScheduleView matches={dashboard.matches} />
          )}
          {section === "tournaments" && (
            <TournamentRegistrationsView
              registrations={tournamentRegistrations}
              loading={tournamentsLoading}
              onOpen={(publicId) => {
                history.pushState({}, "", `/tournaments/${publicId}`);
                location.reload();
              }}
            />
          )}
          {section === "notifications" && (
            <NotificationsView onUnreadChanged={setUnreadNotifications} />
          )}
          {section === "profile" && (
            <ProfileView
              dashboard={dashboard}
              onSwitch={onSwitchOrganization}
              onSaved={async (profile) => {
                const saved = auth.configured
                  ? await memberApi.updateProfile(profile)
                  : profile;
                const updated = { ...dashboard, profile: saved };
                const organizationId =
                  await offlineStore.getPreference("organizationId");
                setDashboard(updated);
                await offlineStore.setPreference(
                  `member-dashboard:${organizationId || "demo"}`,
                  updated,
                );
                return saved;
              }}
            />
          )}
        </main>
        <nav className="bottom-nav" aria-label="Primary navigation">
          <NavButton
            active={section === "home"}
            onClick={() => setSection("home")}
            icon={<Home />}
            label="Home"
          />
          <NavButton
            active={section === "schedule"}
            onClick={() => setSection("schedule")}
            icon={<CalendarDays />}
            label="Schedule"
          />
          <NavButton
            prominent
            active={section === "match"}
            onClick={() => setSection("match")}
            icon={<Radio />}
            label="Match"
          />
          <NavButton
            active={section === "notifications"}
            onClick={() => setSection("notifications")}
            icon={
              <span className="notification-nav-icon">
                <Bell />
                {unreadNotifications > 0 && (
                  <i>{unreadNotifications > 9 ? "9+" : unreadNotifications}</i>
                )}
              </span>
            }
            label="Updates"
          />
          <NavButton
            active={section === "profile"}
            onClick={() => setSection("profile")}
            icon={<UserRound />}
            label="Me"
          />
        </nav>
      </div>
    </>
  );
}

function AuthenticatedMobileApp() {
  const [ready, setReady] = useState(false);
  const [entered, setEntered] = useState(false);
  const [account, setAccount] = useState(null);
  const [online, setOnline] = useState(globalThis.navigator?.onLine !== false);
  const [organizations, setOrganizations] = useState(null);
  const [memberContext, setMemberContext] = useState(null);
  const install = useInstallPrompt();

  useEffect(() => {
    const start = async () => {
      if ("serviceWorker" in navigator && import.meta.env.PROD)
        await navigator.serviceWorker.register("/service-worker.js");
      try {
        applyPlatformIdentity(await memberApi.platformIdentity());
      } catch {
        applyPlatformIdentity();
      }
      setAccount(await auth.currentAccount());
      setEntered(
        Boolean(await offlineStore.getPreference("entered")) ||
          Boolean(claimTokenFromLocation()) ||
          Boolean(teamInviteTokenFromLocation()) ||
          Boolean(teamJoinIdFromLocation()) ||
          Boolean(teamRegistrationIdFromLocation()) ||
          Boolean(tournamentIdFromLocation()),
      );
      setReady(true);
    };
    start();
    const wentOnline = () => {
      setOnline(true);
      syncQueuedOperations().catch(() => {});
    };
    const wentOffline = () => setOnline(false);
    addEventListener("online", wentOnline);
    addEventListener("offline", wentOffline);
    return () => {
      removeEventListener("online", wentOnline);
      removeEventListener("offline", wentOffline);
    };
  }, []);

  useEffect(() => {
    if (
      !ready ||
      !account ||
      !auth.configured ||
      claimTokenFromLocation() ||
      teamInviteTokenFromLocation() ||
      teamJoinIdFromLocation() ||
      teamRegistrationIdFromLocation() ||
      tournamentIdFromLocation()
    )
      return;
    memberApi
      .organizations()
      .then(setOrganizations)
      .catch((error) => {
        if (error.status === 404) setOrganizations([]);
      });
  }, [ready, account]);

  useEffect(() => {
    if (organizations?.length === 1 && !memberContext)
      memberApi.selectOrganization(organizations[0]).then(setMemberContext);
  }, [organizations, memberContext]);

  if (!ready)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>OCHE</b>
      </div>
    );
  const claimToken = claimTokenFromLocation();
  if (claimToken)
    return (
      <ClaimScreen
        token={claimToken}
        account={account}
        onClaimed={(context) => {
          setMemberContext(context);
          history.replaceState({}, "", "/");
          setEntered(true);
        }}
      />
    );
  const teamInviteToken = teamInviteTokenFromLocation();
  if (teamInviteToken)
    return <TeamInvitationScreen token={teamInviteToken} account={account} />;
  const teamJoinId = teamJoinIdFromLocation();
  if (teamJoinId)
    return <TeamJoinScreen publicId={teamJoinId} account={account} />;
  const teamRegistrationId = teamRegistrationIdFromLocation();
  if (teamRegistrationId)
    return (
      <TeamRegistrationScreen publicId={teamRegistrationId} account={account} />
    );
  const tournamentId = tournamentIdFromLocation();
  if (tournamentId)
    return (
      <TournamentRegistrationScreen publicId={tournamentId} account={account} />
    );
  if (!entered)
    return (
      <Welcome
        install={install}
        onContinue={async () => {
          await offlineStore.setPreference("entered", true);
          if (!account) await auth.signIn("/");
          else setEntered(true);
        }}
      />
    );
  if (auth.configured && !account)
    return <Welcome install={install} onContinue={() => auth.signIn("/")} />;
  if (auth.configured && organizations?.length === 0 && !memberContext)
    return <InviteNeeded account={account} online={online} install={install} />;
  if (auth.configured && organizations?.length > 1 && !memberContext)
    return (
      <>
        <BrowserInstallRecommendation install={install} />
        <OrganizationPicker
          organizations={organizations}
          onSelect={async (organization) =>
            setMemberContext(await memberApi.selectOrganization(organization))
          }
        />
      </>
    );
  if (auth.configured && organizations?.length === 1 && !memberContext)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>LOADING YOUR LEAGUE</b>
      </div>
    );
  return (
    <Shell
      account={memberContext?.account || account}
      organizationName={memberContext?.organizationName}
      online={online}
      install={install}
      onSwitchOrganization={async () => {
        await offlineStore.removePreference("member-dashboard");
        setMemberContext(null);
        setOrganizations(await memberApi.organizations());
      }}
    />
  );
}

export default function MobileApp() {
  if (/^\/sms-opt-in\/?$/i.test(routeFromLocation())) return <SmsOptInDemo />;
  return (
    <>
      <PullToRefresh />
      <AuthenticatedMobileApp />
    </>
  );
}
